import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Editor from '@monaco-editor/react';

// ✅ FIX 1: Single place to change API URL for deployment
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function LatexEditor({ user: propUser, showFeedback, onFeedbackClose }) {
  const location = useLocation();
  const [latexCode, setLatexCode] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isCompiling, setIsCompiling] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [showErrorLog, setShowErrorLog] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [templates, setTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projects, setProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '', 'saving', 'saved'
  
  const [user, setUser] = useState(propUser);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState(null);
  
  // File Management States
  const [fileTree, setFileTree] = useState([]);
  const [currentFilePath, setCurrentFilePath] = useState('main.tex');
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root']));
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showCreateFileModal, setShowCreateFileModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFileName, setNewFileName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [newName, setNewName] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedParentPath, setSelectedParentPath] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDestination, setMoveDestination] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  const fileInputRef = useRef(null);
  // Feedback state managed by App.js via props
  const [feedbackText, setFeedbackText] = useState('');
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [isUploadingZip, setIsUploadingZip] = useState(false);
  const zipInputRef = useRef(null);
  const [showZipProjectModal, setShowZipProjectModal] = useState(false);
  const [zipFile, setZipFile] = useState(null);
  const [zipProjectName, setZipProjectName] = useState('');
  const imageInputRef = useRef(null);
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const uploadingRef = useRef(false); // Guard against duplicate uploads
  const [isDarkTheme, setIsDarkTheme] = useState(() => localStorage.getItem('editorTheme') === 'dark');
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  useEffect(() => {
    if (propUser) {
      setUser(propUser);
      fetchTemplates();
      // Auto-restore last project after user is set
      restoreLastProject();
    } else {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        setUser(JSON.parse(userData));
        fetchTemplates();
        // Auto-restore last project after user is set
        restoreLastProject();
      } else {
        setShowAuthModal(true);
        fetchTemplates();
      }
    }
  }, [propUser]);

  // Auto-load project when navigated from ConvertPage
  useEffect(() => {
    const openId = location.state?.openProjectId;
    if (openId && user) {
      loadProject(openId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, user]);

  // Save current project to localStorage for session persistence
  useEffect(() => {
    if (currentProjectId) {
      localStorage.setItem('lastProjectId', currentProjectId);
      const currentProject = projects.find(p => p.id === currentProjectId);
      if (currentProject) {
        localStorage.setItem('lastProjectName', currentProject.name);
      }
    }
  }, [currentProjectId, projects]);

  // Auto-save current file content when editor changes
  useEffect(() => {
    if (currentProjectId && currentFilePath && latexCode !== '') {
      const timer = setTimeout(() => {
        saveCurrentFileContent();
      }, 1000); // Debounce for 1 second
      
      return () => clearTimeout(timer);
    }
  }, [latexCode, currentProjectId, currentFilePath]);

  // Fetch file tree when project loads
  useEffect(() => {
    if (currentProjectId) {
      fetchFileTree();
    }
  }, [currentProjectId]);

  // Close context menu and dropdowns on click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setShowUploadDropdown(false);
      setShowExportDropdown(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Suppress Monaco Editor CDN error (known bug in v0.55.1)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      // Suppress Monaco offsetNode error
      if (args[0]?.toString().includes('offsetNode') || 
          args[0]?.toString().includes('monaco-editor')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S - Save Project
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (user) {
          if (currentProjectId) {
            saveCurrentFileContent();
            showToast('💾 Project saved!', 'success');
          } else {
            setShowSaveModal(true);
          }
        } else {
          alert('Please login to save projects');
        }
      }
      
      // Ctrl+Enter or Cmd+Enter - Compile PDF
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (latexCode && !isCompiling) {
          compilePDF();
        }
      }
      
      // Ctrl+D or Cmd+D - Download PDF (if available)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        if (pdfUrl) {
          downloadPDF();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [latexCode, isCompiling, pdfUrl, user, currentProjectId]);

  useEffect(() => {
    localStorage.setItem('editorTheme', isDarkTheme ? 'dark' : 'light');
  }, [isDarkTheme]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  // ✅ FIX: Central fetch wrapper that catches expired token errors
  const authFetch = async (url, options = {}) => {
    const response = await fetch(url, options);
    if (response.status === 401 || response.status === 403) {
      // Token expired — clear session and force re-login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('lastProjectId');
      setUser(null);
      setCurrentProjectId(null);
      setShowAuthModal(true);
      if (localStorage.getItem('token')) showToast('⚠️ Session expired. Please login again.', 'error');
      throw new Error('Session expired');
    }
    return response;
  };

  // ==================== FILE MANAGEMENT FUNCTIONS ====================

  const fetchFileTree = async () => {
    if (!currentProjectId) return;
    
    try {
      console.log('🔍 Fetching file tree for project:', currentProjectId);
      const response = await authFetch(`${API_BASE}/api/projects/${currentProjectId}/files`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      console.log('📦 Received data from backend:', data);
      
      if (data.success && data.files) {
        console.log('✅ Setting file tree:', data.files);
        setFileTree(data.files);
      } else {
        console.log('❌ Unexpected data format:', data);
      }
    } catch (err) {
      console.error('❌ Failed to fetch file tree:', err);
    }
  };

  const saveCurrentFileContent = async () => {
    if (!currentProjectId || !currentFilePath) return;
    
    try {
      setAutoSaveStatus('saving');
      await authFetch(`${API_BASE}/api/projects/${currentProjectId}/file-content`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          filePath: currentFilePath,
          content: latexCode
        })
      });
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (err) {
      console.error('Failed to auto-save file:', err);
      setAutoSaveStatus('');
    }
  };

  const loadFileContent = async (filePath) => {
    if (!currentProjectId) return;
    
    try {
      // Save current file before switching
      if (currentFilePath) {
        await saveCurrentFileContent();
      }
      
      const response = await fetch(
        `${API_BASE}/api/projects/${currentProjectId}/file-content?path=${encodeURIComponent(filePath)}`,
        {
          headers: getAuthHeaders()
        }
      );
      const data = await response.json();
      
      if (data.success && data.content !== undefined) {
        setLatexCode(data.content);
        setCurrentFilePath(filePath);
        setPdfUrl(null);
      } else {
        setError(data.error || 'Failed to load file');
      }
    } catch (err) {
      console.error('Failed to load file:', err);
      setError('Failed to load file');
    }
  };

  const handleCreateFolder = async () => {
    if (!currentProjectId || !newFolderName.trim()) {
      setError('Please enter a folder name');
      return;
    }
    
    console.log('📁 Creating folder:', newFolderName, 'in parent:', selectedParentPath);
    
    try {
      const response = await authFetch(`${API_BASE}/api/projects/${currentProjectId}/folders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          folderPath: selectedParentPath ? `${selectedParentPath}/${newFolderName}` : newFolderName
        })
      });
      
      const data = await response.json();
      console.log('📦 Create folder response:', data);
      
      if (data.limitReached) {
        const msg = data.limitType === 'projects'
          ? '⚠️ Project limit reached (5 projects)! Upgrade to Pro for unlimited projects.'
          : '⚠️ Limit reached! Upgrade to Pro.';
        showToast(msg, 'error');
        return;
      }
      if (data.success) {
        console.log('✅ Folder created successfully, refreshing tree...');
        setShowCreateFolderModal(false);
        setNewFolderName('');
        setSelectedParentPath('');
        await fetchFileTree();
      } else {
        console.log('❌ Failed to create folder:', data.error);
        setError(data.error || 'Failed to create folder');
      }
    } catch (err) {
      console.error('❌ Error creating folder:', err);
      setError('Failed to create folder: ' + err.message);
    }
  };

  // Helper function to check if file exists in tree
  const checkFileExists = (tree, targetPath) => {
    for (const item of tree) {
      if (item.path === targetPath) {
        return true;
      }
      if (item.children && item.children.length > 0) {
        if (checkFileExists(item.children, targetPath)) {
          return true;
        }
      }
    }
    return false;
  };

  const handleCreateFile = async () => {
    if (!currentProjectId || !newFileName.trim()) {
      setError('Please enter a file name');
      return;
    }
    
    // Ensure .tex extension
    const fileName = newFileName.endsWith('.tex') ? newFileName : `${newFileName}.tex`;
    const filePath = selectedParentPath ? `${selectedParentPath}/${fileName}` : fileName;
    
    // Check for duplicate filename
    const fileExists = checkFileExists(fileTree, filePath);
    if (fileExists) {
      setError(`File "${fileName}" already exists!`);
      return;
    }
    
    try {
      const response = await authFetch(`${API_BASE}/api/projects/${currentProjectId}/file-content`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          filePath: filePath,
          content: '% New LaTeX file\n\\documentclass{article}\n\\begin{document}\n\n\\end{document}'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowCreateFileModal(false);
        setNewFileName('');
        setSelectedParentPath('');
        await fetchFileTree();
        await loadFileContent(filePath);
      } else {
        setError(data.error || 'Failed to create file');
      }
    } catch (err) {
      setError('Failed to create file: ' + err.message);
    }
  };

  const handleRename = async () => {
    if (!currentProjectId || !renameTarget || !newName.trim()) {
      setError('Please enter a new name');
      return;
    }
    
    try {
      const response = await authFetch(`${API_BASE}/api/projects/${currentProjectId}/rename`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          oldPath: renameTarget.path,
          newName: newName  // FIXED: Changed from newPath to newName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast('✏️ Renamed successfully!', 'success');  // ADDED: Toast notification
        setShowRenameModal(false);
        setRenameTarget(null);
        setNewName('');
        await fetchFileTree();
        
        // Update current file path if the renamed item is the current file
        if (currentFilePath === renameTarget.path) {
          setCurrentFilePath(renameTarget.path.replace(/[^/]+$/, newName));
        }
      } else {
        setError(data.error || 'Failed to rename');
      }
    } catch (err) {
      setError('Failed to rename: ' + err.message);
    }
  };
  
  const handleDeleteFile = async (filePath, isFolder = false) => {
    const itemType = isFolder ? 'folder' : 'file';
    if (!window.confirm(`Delete ${itemType} "${filePath}"?`)) {
      return;
    }
    
    try {
      const response = await authFetch(`${API_BASE}/api/projects/${currentProjectId}/files`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ filePath })
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchFileTree();
        
        // If deleted file is current file, clear editor
        if (currentFilePath === filePath) {
          setLatexCode('');
          setCurrentFilePath('');
        }
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  // Handle image conversion
  const handleConvertImage = async (imagePath, targetFormat) => {
    if (!currentProjectId) return;
    
    setContextMenu(null);
    
    try {
      const response = await authFetch(`${API_BASE}/api/projects/${currentProjectId}/convert-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          imagePath: imagePath,
          targetFormat: targetFormat
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast(`🔄 Image converted to ${targetFormat.toUpperCase()}!`, 'success');
        await fetchFileTree();
      } else {
        setError(data.error || 'Failed to convert image');
      }
    } catch (err) {
      setError('Failed to convert image: ' + err.message);
    }
  };

  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const handleContextMenu = (e, item, parentPath = '') => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item: item,
      parentPath: parentPath
    });
  };

  const openCreateFolderModal = (parentPath = '') => {
    setSelectedParentPath(parentPath);
    setShowCreateFolderModal(true);
    setContextMenu(null);
  };

  const openCreateFileModal = (parentPath = '') => {
    setSelectedParentPath(parentPath);
    setShowCreateFileModal(true);
    setContextMenu(null);
  };

  // ✅ Move file to another folder
  const handleMove = async () => {
    if (!moveTarget) return;
    const fileName = moveTarget.name;
    const newPath = moveDestination
      ? `${moveDestination}/${fileName}`
      : fileName;

    try {
      const response = await authFetch(
        `${API_BASE}/api/projects/${currentProjectId}/rename`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ oldPath: moveTarget.path, newName: newPath })
        }
      );
      const data = await response.json();
      if (data.success) {
        showToast(`✅ Moved to ${moveDestination || 'root'}`, 'success');
        await fetchFileTree();
      } else {
        setError(data.error || 'Failed to move file');
      }
    } catch (err) {
      if (err.message !== 'Session expired')
        setError('Failed to move: ' + err.message);
    } finally {
      setShowMoveModal(false);
      setMoveTarget(null);
      setMoveDestination('');
    }
  };

  const openRenameModal = (item) => {
    setRenameTarget(item);
    setNewName(item.name);
    setShowRenameModal(true);
    setContextMenu(null);
  };

  // ==================== RENDER FILE TREE ====================

  const renderFileTreeItem = (item, level = 0, parentPath = '') => {
    const itemPath = parentPath ? `${parentPath}/${item.name}` : item.name;
    const isExpanded = expandedFolders.has(itemPath);
    const isCurrentFile = itemPath === currentFilePath;
    
    if (item.type === 'folder') {
      return (
        <div key={itemPath} style={{ marginLeft: `${level * 15}px` }}>
          <div
            onClick={() => toggleFolder(itemPath)}
            onContextMenu={(e) => handleContextMenu(e, { ...item, path: itemPath }, parentPath)}
            style={{
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '4px',
              background: 'transparent',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = isDarkTheme ? '#2a2a40' : '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{ fontSize: '14px' }}>
              {isExpanded ? '📂' : '📁'}
            </span>
            <span style={{ fontSize: '14px', color: isDarkTheme ? '#cdd6f4' : '#334155', fontWeight: '500' }}>
              {item.name}
            </span>
          </div>
          
          {isExpanded && item.children && (
            <div>
              {item.children.map(child => renderFileTreeItem(child, level + 1, itemPath))}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          key={itemPath}
          onClick={() => loadFileContent(itemPath)}
          onContextMenu={(e) => handleContextMenu(e, { ...item, path: itemPath }, parentPath)}
          style={{
            marginLeft: `${level * 15}px`,
            padding: '6px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '4px',
            background: isCurrentFile ? (isDarkTheme ? '#1d3254' : '#dbeafe') : 'transparent',
            borderLeft: isCurrentFile ? '3px solid #3b82f6' : '3px solid transparent',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isCurrentFile) e.currentTarget.style.background = isDarkTheme ? '#2a2a40' : '#f1f5f9';
          }}
          onMouseLeave={(e) => {
            if (!isCurrentFile) e.currentTarget.style.background = 'transparent';
          }}
        >
          <span style={{ fontSize: '14px' }}>
            {item.name.endsWith('.tex') ? '📄' : '📋'}
          </span>
          <span style={{
            fontSize: '14px',
            color: isCurrentFile ? (isDarkTheme ? '#60a5fa' : '#1e40af') : (isDarkTheme ? '#a0aec0' : '#475569'),
            fontWeight: isCurrentFile ? '600' : '400'
          }}>
            {item.name}
          </span>
        </div>
      );
    }
  };

  // ==================== AUTH FUNCTIONS ====================

  const handleLogin = async () => {
    if (!authForm.email || !authForm.password) {
      setError('Email and password required');
      return;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authForm.email,
          password: authForm.password
        })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        setShowAuthModal(false);
        setAuthForm({ name: '', email: '', password: '' });
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Login failed: ' + err.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSignup = async () => {
    if (!authForm.name || !authForm.email || !authForm.password) {
      setError('All fields required');
      return;
    }

    if (authForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(authForm)
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        setShowAuthModal(false);
        setAuthForm({ name: '', email: '', password: '' });
      } else {
        setError(data.error || 'Signup failed');
      }
    } catch (err) {
      setError('Signup failed: ' + err.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setProjects([]);
    setLatexCode('');
    setPdfUrl(null);
    setUploadedImages([]);
    setFileTree([]);
    setCurrentProjectId(null);
    setCurrentFilePath('');
    setShowAuthModal(true);
  };

  // ==================== PROJECT FUNCTIONS ====================

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/templates`);
      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const fetchProjects = async () => {
    if (!user) {
      setError('Please login to view projects');
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/projects`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      // ✅ FIX: ensure data is always an array
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects');
    }
  };

  const saveProject = async () => {
    if (!user) {
      setError('Please login to save projects');
      setShowAuthModal(true);
      return;
    }

    // If project already exists — save silently without modal
    if (currentProjectId) {
      try {
        await authFetch(`${API_BASE}/api/projects/save`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            projectId: currentProjectId,
            projectName: projectName || 'Untitled Project',
            texContent: latexCode,
            templateId: currentTemplateId,
            images: uploadedImages
          })
        });
        showToast('💾 Project saved!', 'success');
      } catch (err) {
        setError('Failed to save: ' + err.message);
      }
      return;
    }

    // New project — show modal to get name
    if (!projectName.trim()) {
      setError('Please enter a project name');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await authFetch(`${API_BASE}/api/projects/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          projectName: projectName,
          texContent: latexCode,
          templateId: currentTemplateId,
          images: uploadedImages
        })
      });

      const data = await response.json();

      if (data.success) {
        setCurrentProjectId(data.projectId);
        setShowSaveModal(false);
        setProjectName('');
        showToast('💾 Project saved successfully!', 'success');
        await fetchFileTree();
      } else {
        setError(data.error || 'Failed to save project');
      }
    } catch (err) {
      setError('Failed to save project: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const loadProject = async (projectId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      setLatexCode(data.texContent);
      setCurrentTemplateId(data.metadata.templateId);
      setUploadedImages([]); // ✅ FIX: don't restore old images list, file tree shows them
      setCurrentProjectId(projectId);
      // Use mainTexFile from metadata, fallback to main.tex
      const mainFile = data.metadata?.mainTexFile || 'main.tex';
      setCurrentFilePath(mainFile);
      setShowProjectsModal(false);
      setPdfUrl(null);
    } catch (err) {
      console.error('Failed to load project:', err);
      setError('Failed to load project');
    }
  };

  const restoreLastProject = async () => {
    const lastProjectId = localStorage.getItem('lastProjectId');
    const lastProjectName = localStorage.getItem('lastProjectName');
    
    if (lastProjectId && user) {
      try {
        // Silently try to restore last project
        const response = await authFetch(`${API_BASE}/api/projects/${lastProjectId}`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Restore project silently
          setLatexCode(data.texContent);
          setCurrentTemplateId(data.metadata.templateId);
          setUploadedImages([]); // ✅ FIX: clear on restore
          setCurrentProjectId(lastProjectId);
          const mainFile = data.metadata?.mainTexFile || 'main.tex';
          setCurrentFilePath(mainFile);
          setPdfUrl(null);
          
          // Show toast notification
          showToast(`📂 Restored: ${lastProjectName || 'Last Project'}`, 'success');
        } else {
          // Project no longer exists, clear localStorage
          localStorage.removeItem('lastProjectId');
          localStorage.removeItem('lastProjectName');
        }
      } catch (err) {
        console.error('Failed to restore last project:', err);
        // Silently fail - don't show error to user
        localStorage.removeItem('lastProjectId');
        localStorage.removeItem('lastProjectName');
      }
    }
  };

  const deleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Delete project "${projectName}"?`)) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        fetchProjects();
        alert('✅ Project deleted!');
      } else {
        setError(data.error || 'Failed to delete project');
      }
    } catch (err) {
      setError('Failed to delete project: ' + err.message);
    }
  };

  // ==================== TEMPLATE & COMPILATION ====================

  const categories = ['All', ...new Set(templates.map(t => t.category))];

  const filteredTemplates = selectedCategory === 'All' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const loadTemplate = async (templateId) => {
    try {
      const response = await fetch(`${API_BASE}/api/templates/${templateId}`);
      const data = await response.json();
      setLatexCode(data.content);
      setCurrentTemplateId(templateId);
      setShowTemplates(false);
      setPdfUrl(null);
      setUploadedImages([]);
      setCurrentProjectId(null);
      setFileTree([]);
      setCurrentFilePath('');
    } catch (err) {
      console.error('Failed to load template:', err);
      setError('Failed to load template content');
    }
  };


  const sendFeedback = async () => {
    if (!feedbackText.trim()) {
      setError('Please enter feedback');
      return;
    }
    setIsSendingFeedback(true);
    try {
      const response = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          feedback: feedbackText,
          userEmail: user?.email || 'anonymous',
          timestamp: new Date().toISOString()
        })
      });
      const data = await response.json();
      if (data.success) {
        showToast('💬 Thank you for feedback!', 'success');
        if (onFeedbackClose) onFeedbackClose();
        setFeedbackText('');
      }
    } catch (err) {
      setError('Failed to send feedback');
    } finally {
      setIsSendingFeedback(false);
    }
  };


  const handleZipUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      setError('Please upload ZIP file');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) {
      setError('ZIP too large! Max 50MB');
      return;
    }
    
    if (!user) {
      setError('Please login first');
      setShowAuthModal(true);
      return;
    }
    
    // Store file and show project name modal
    setZipFile(file);
    setShowZipProjectModal(true);
  };
  
  const handleZipProjectSave = async () => {
    if (!zipProjectName.trim()) {
      setError('Please enter project name');
      return;
    }
    
    setIsUploadingZip(true);
    setShowZipProjectModal(false);
    
    try {
      const formData = new FormData();
      formData.append('zipfile', zipFile);
      formData.append('projectName', zipProjectName);
      
      const response = await authFetch(`${API_BASE}/api/projects/create-from-zip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast('📦 Project created from ZIP!', 'success');
        setCurrentProjectId(data.projectId);
        setProjectName(zipProjectName);
        
        // Fetch file tree first
        await fetchFileTree();
        
        // Load the main tex file returned by backend
        const mainFile = data.mainTexFile || 'main.tex';
        
        try {
          const fileResponse = await authFetch(`${API_BASE}/api/projects/${data.projectId}/file-content?path=${encodeURIComponent(mainFile)}`, {
            headers: getAuthHeaders()
          });
          
          if (fileResponse.ok) {
            const fileData = await fileResponse.json();
            setLatexCode(fileData.content);
            setCurrentFilePath(mainFile);
          }
        } catch (err) {
          console.log('Could not load main file:', err);
        }
      } else {
        setError(data.error || 'Failed to create project from ZIP');
      }
    } catch (err) {
      setError('Failed to upload ZIP: ' + err.message);
    } finally {
      setIsUploadingZip(false);
      setZipFile(null);
      setZipProjectName('');
      if (zipInputRef.current) zipInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (uploadingRef.current) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please upload an image file (PNG, JPG, PDF)');
      return;
    }

    // ✅ FIX: Auto-create project if not saved yet
    let projectId = currentProjectId;
    if (!projectId) {
      if (!user) {
        setError('Please login to upload images');
        setShowAuthModal(true);
        return;
      }
      try {
        showToast('⏳ Auto-saving project...', 'success');
        const saveRes = await authFetch(`${API_BASE}/api/projects/save`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            projectName: 'Untitled Project',
            texContent: latexCode || '\\documentclass{article}\n\\begin{document}\n\n\\end{document}',
            templateId: currentTemplateId,
            images: []
          })
        });
        const saveData = await saveRes.json();
        if (saveData.success) {
          projectId = saveData.projectId;
          setCurrentProjectId(projectId);
          setProjectName('Untitled Project');
          await fetchFileTree();
        } else {
          setError('Please save your project first');
          return;
        }
      } catch (err) {
        setError('Please save your project first');
        return;
      }
    }

    uploadingRef.current = true;
    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await authFetch(`${API_BASE}/api/projects/${projectId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        await fetchFileTree();
        const imagePath = data.path;
        showToast(`🖼️ Image uploaded: ${imagePath}`, 'success');
      } else {
        setError(data.error || 'Failed to upload image');
      }
    } catch (err) {
      setError('Failed to upload image: ' + err.message);
    } finally {
      setIsUploading(false);
      uploadingRef.current = false;
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000); // Auto-hide after 3 seconds
  };

  const compilePDF = async () => {
    setIsCompiling(true);
    setError(null);
    setErrorDetails(null);
    
    try {
      let endpoint = `${API_BASE}/api/compile`;
      let body = {
        texContent: latexCode,
        templateId: currentTemplateId,
        projectId: currentProjectId,
        images: uploadedImages
      };
      
      // If we have a project with main file, use project-based compilation
      if (currentProjectId && currentFilePath) {
        // Save current file content before compiling
        await authFetch(`${API_BASE}/api/projects/${currentProjectId}/file-content`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ filePath: currentFilePath, content: latexCode })
        });
        endpoint = `${API_BASE}/api/projects/${currentProjectId}/compile`;
        body = {
          mainFile: currentFilePath
        };
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        const pdfBlob = base64ToBlob(data.pdf, 'application/pdf');
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
        showToast('✅ PDF compiled successfully!', 'success');
      } else if (data.limitReached) {
        const msg = data.limitType === 'guest'
          ? '⚠️ Daily limit reached (3/day)! Sign up free to get 20 compilations/month.'
          : '⚠️ Monthly limit reached (20/month)! <a href="https://latexlab.lemonsqueezy.com/checkout/buy/1a74bd46-1574-43f3-b0c5-e827847c6462?desc=0" target="_blank">Upgrade to Pro</a> for unlimited compilations.';
        showToast(msg, 'error');
        setError(msg);
      } else {
        setError(data.error || 'Compilation failed');
        setErrorDetails(parseLatexErrors(data.log || ''));
      }
    } catch (err) {
      setError('Failed to connect to backend: ' + err.message);
    } finally {
      setIsCompiling(false);
    }
  };

  const base64ToBlob = (base64, type) => {
    const binStr = atob(base64);
    const len = binStr.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      arr[i] = binStr.charCodeAt(i);
    }
    return new Blob([arr], { type });
  };

  const parseLatexErrors = (log) => {
    const lines = log.split('\n');
    const errors = [];
    
    // Common LaTeX error patterns with helpful solutions
    const errorPatterns = [
      {
        pattern: /Undefined control sequence/i,
        hint: '❌ Undefined command. Check if you spelled the command correctly or need to add a package (e.g., \\usepackage{amsmath} for math commands).'
      },
      {
        pattern: /Missing \\begin{document}/i,
        hint: '❌ Missing \\begin{document}. Every LaTeX file must have \\begin{document} and \\end{document}.'
      },
      {
        pattern: /File .* not found/i,
        hint: '❌ File not found. Make sure image/file exists and the filename is correct (LaTeX is case-sensitive).'
      },
      {
        pattern: /Missing \\$ inserted/i,
        hint: '❌ Math mode error. You might have used math symbols like _, ^, or \\ outside of $ $ or \\[ \\]. Wrap math in dollar signs.'
      },
      {
        pattern: /Runaway argument/i,
        hint: '❌ Missing closing brace }. Check that every { has a matching }.'
      },
      {
        pattern: /Environment .* undefined/i,
        hint: '❌ Unknown environment. You might need to add a package. For example, \\usepackage{amsmath} for align, equation, etc.'
      },
      {
        pattern: /Missing } inserted/i,
        hint: '❌ Missing closing brace }. Count your braces - every { needs a }.'
      },
      {
        pattern: /Illegal parameter number/i,
        hint: '❌ Wrong use of #. The # character is special in LaTeX. Use \\# if you want to display it.'
      },
      {
        pattern: /Package .* Error/i,
        hint: '❌ Package error. The error message above shows which package is causing issues.'
      },
      {
        pattern: /LaTeX Error: Unknown graphics extension/i,
        hint: '❌ Unsupported image format. Use .png, .jpg, or .pdf images. Make sure to include the file extension.'
      }
    ];
    
    // Parse errors from log
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Look for error lines starting with !
      if (line.startsWith('!')) {
        const errorMessage = line.substring(2);
        let hint = '💡 Check the LaTeX syntax around this line.';
        
        // Find matching hint for this error
        for (const pattern of errorPatterns) {
          if (pattern.pattern.test(errorMessage)) {
            hint = pattern.hint;
            break;
          }
        }
        
        // Try to extract line number
        let lineNumber = null;
        const lineMatch = lines[i + 1]?.match(/l\.(\d+)/);
        if (lineMatch) {
          lineNumber = lineMatch[1];
        }
        
        errors.push({
          type: 'error',
          message: errorMessage,
          hint: hint,
          lineNumber: lineNumber,
          context: lines[i + 1] || ''
        });
      }
    }
    
    // If no specific errors found but compilation failed
    if (errors.length === 0) {
      // Check for common issues in the log
      if (log.includes('Emergency stop')) {
        return [{
          type: 'error',
          message: 'LaTeX encountered a critical error and stopped.',
          hint: '❌ There might be a severe syntax error. Check for missing \\end{document}, unmatched braces, or corrupted commands.',
          context: ''
        }];
      }
      
      if (log.includes('No pages of output')) {
        return [{
          type: 'error',
          message: 'No PDF was generated.',
          hint: '❌ The document compiled but produced no output. Make sure you have content between \\begin{document} and \\end{document}.',
          context: ''
        }];
      }
      
      // Generic error
      return [{
        type: 'error',
        message: 'Compilation failed.',
        hint: '💡 Check your LaTeX syntax. Common issues: missing packages, unmatched braces {}, missing $ for math, or typos in commands.',
        context: log.substring(0, 500)
      }];
    }
    
    return errors;
  };


  const downloadZIP = async () => {
    if (!currentProjectId) {
      setError('No project loaded — please save your project first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/api/projects/${currentProjectId}/download-zip`,
        { headers: { 'Authorization': token ? `Bearer ${token}` : '' } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${projectName || 'project'}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast('📦 ZIP downloaded!', 'success');
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to download ZIP');
      }
    } catch (err) {
      setError('Failed to download ZIP: ' + err.message);
    }
  };

  const downloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'document.pdf';
      link.click();
    }
  };

  // ✅ NEW: Download source .tex file
  const downloadTeX = () => {
    if (!latexCode) return;
    const blob = new Blob([latexCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName || 'document'}.tex`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.tex')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLatexCode(e.target.result);
        setCurrentTemplateId(null);
        setUploadedImages([]);
        setCurrentProjectId(null);
        setFileTree([]);
        setCurrentFilePath('');
      };
      reader.readAsText(file);
    } else {
      setError('Please upload a .tex file');
    }
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.onDidBlurEditorText(() => {
      savedSelectionRef.current = editor.getSelection();
    });
    
    // Register LaTeX auto-completion
    monaco.languages.registerCompletionItemProvider('latex', {
      triggerCharacters: ['\\'],
      provideCompletionItems: (model, position) => {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });
        const cmdMatch = textUntilPosition.match(/\\[a-zA-Z]*$/);
        const word = model.getWordUntilPosition(position);
        const range = cmdMatch
          ? {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: position.column - cmdMatch[0].length,
              endColumn: position.column,
            }
          : {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

        const K = monaco.languages.CompletionItemKind;
        const R = monaco.languages.CompletionItemInsertTextRule;
        const snip = (label, insertText, doc) => ({ label, insertText, documentation: doc, range, kind: K.Snippet, insertTextRules: R.InsertAsSnippet });
        const kw   = (label, insertText, doc) => ({ label, insertText, documentation: doc, range, kind: K.Keyword });

        const suggestions = [
          // ── Document structure ──────────────────────────────────────────
          snip('\\documentclass{}',           '\\documentclass[${1:12pt}]{${2:article}}',                                                    'Document class declaration'),
          snip('\\usepackage{}',              '\\usepackage{$1}',                                                                             'Import package'),
          snip('\\usepackage[]{}',            '\\usepackage[${1:options}]{${2:package}}',                                                     'Import package with options'),
          snip('\\begin{document}',           '\\begin{document}\n\t$0\n\\end{document}',                                                     'Document environment'),
          snip('\\title{}',                   '\\title{$1}',                                                                                   'Document title'),
          snip('\\author{}',                  '\\author{$1}',                                                                                  'Document author'),
          snip('\\date{}',                    '\\date{$1}',                                                                                    'Document date'),
          kw  ('\\maketitle',                 '\\maketitle',                                                                                   'Render title block'),
          kw  ('\\tableofcontents',           '\\tableofcontents',                                                                            'Insert table of contents'),
          kw  ('\\listoffigures',             '\\listoffigures',                                                                              'Insert list of figures'),
          kw  ('\\listoftables',              '\\listoftables',                                                                               'Insert list of tables'),
          snip('\\input{}',                   '\\input{$1}',                                                                                   'Input another file'),
          snip('\\include{}',                 '\\include{$1}',                                                                                 'Include another file (with page break)'),
          snip('\\includeonly{}',             '\\includeonly{$1}',                                                                             'Restrict included files'),
          snip('\\newcommand{}{}',            '\\newcommand{\\${1:cmd}}[${2:0}]{$3}',                                                          'Define new command'),
          snip('\\renewcommand{}{}',          '\\renewcommand{\\${1:cmd}}[${2:0}]{$3}',                                                        'Redefine existing command'),
          snip('\\providecommand{}{}',        '\\providecommand{\\${1:cmd}}[${2:0}]{$3}',                                                      'Provide command if not defined'),
          snip('\\newenvironment{}{}{}',      '\\newenvironment{${1:name}}{${2:begin}}{${3:end}}',                                             'Define new environment'),

          // ── Sectioning ───────────────────────────────────────────────────
          snip('\\part{}',                    '\\part{$1}',                                                                                    'Part heading'),
          snip('\\chapter{}',                 '\\chapter{$1}',                                                                                 'Chapter heading'),
          snip('\\chapter*{}',               '\\chapter*{$1}',                                                                                'Unnumbered chapter'),
          snip('\\section{}',                 '\\section{$1}',                                                                                 'Section heading'),
          snip('\\section*{}',               '\\section*{$1}',                                                                                'Unnumbered section'),
          snip('\\subsection{}',              '\\subsection{$1}',                                                                              'Subsection heading'),
          snip('\\subsection*{}',            '\\subsection*{$1}',                                                                             'Unnumbered subsection'),
          snip('\\subsubsection{}',           '\\subsubsection{$1}',                                                                           'Subsubsection heading'),
          snip('\\paragraph{}',              '\\paragraph{$1}',                                                                              'Paragraph heading'),
          snip('\\subparagraph{}',           '\\subparagraph{$1}',                                                                           'Subparagraph heading'),
          kw  ('\\appendix',                  '\\appendix',                                                                                    'Start appendix'),

          // ── Text formatting ──────────────────────────────────────────────
          snip('\\textbf{}',                  '\\textbf{$1}',                                                                                  'Bold text'),
          snip('\\textit{}',                  '\\textit{$1}',                                                                                  'Italic text'),
          snip('\\emph{}',                    '\\emph{$1}',                                                                                    'Emphasized text'),
          snip('\\underline{}',               '\\underline{$1}',                                                                               'Underlined text'),
          snip('\\texttt{}',                  '\\texttt{$1}',                                                                                  'Typewriter (monospace) text'),
          snip('\\textsc{}',                  '\\textsc{$1}',                                                                                  'Small caps text'),
          snip('\\textrm{}',                  '\\textrm{$1}',                                                                                  'Roman text'),
          snip('\\textsf{}',                  '\\textsf{$1}',                                                                                  'Sans-serif text'),
          snip('\\textsl{}',                  '\\textsl{$1}',                                                                                  'Slanted text'),
          snip('\\textup{}',                  '\\textup{$1}',                                                                                  'Upright text'),
          snip('\\textnormal{}',              '\\textnormal{$1}',                                                                              'Normal text'),
          snip('\\textmd{}',                  '\\textmd{$1}',                                                                                  'Medium weight text'),
          snip('\\mbox{}',                    '\\mbox{$1}',                                                                                    'Box (no line break)'),
          snip('\\sout{}',                    '\\sout{$1}',                                                                                    'Strikethrough (ulem package)'),
          snip('\\uline{}',                   '\\uline{$1}',                                                                                   'Underline (ulem package)'),
          snip('\\footnote{}',                '\\footnote{$1}',                                                                                'Footnote'),
          snip('\\footnotemark',              '\\footnotemark',                                                                                'Footnote mark'),
          snip('\\footnotetext{}',            '\\footnotetext{$1}',                                                                            'Footnote text'),
          kw  ('\\newline',                   '\\newline',                                                                                     'Force new line'),
          kw  ('\\noindent',                  '\\noindent',                                                                                    'Suppress indentation'),
          kw  ('\\indent',                    '\\indent',                                                                                      'Force indentation'),

          // ── Font sizes ───────────────────────────────────────────────────
          kw  ('\\tiny',                      '\\tiny',                                                                                        'Tiny font size'),
          kw  ('\\scriptsize',                '\\scriptsize',                                                                                  'Script size font'),
          kw  ('\\footnotesize',              '\\footnotesize',                                                                                'Footnote size font'),
          kw  ('\\small',                     '\\small',                                                                                       'Small font size'),
          kw  ('\\normalsize',                '\\normalsize',                                                                                  'Normal font size'),
          kw  ('\\large',                     '\\large',                                                                                       'Large font size'),
          kw  ('\\Large',                     '\\Large',                                                                                       'Larger font size'),
          kw  ('\\LARGE',                     '\\LARGE',                                                                                       'Even larger font size'),
          kw  ('\\huge',                      '\\huge',                                                                                        'Huge font size'),
          kw  ('\\Huge',                      '\\Huge',                                                                                        'Largest font size'),

          // ── Lists ────────────────────────────────────────────────────────
          snip('\\begin{itemize}',            '\\begin{itemize}\n\t\\item $0\n\\end{itemize}',                                                 'Bullet list'),
          snip('\\begin{enumerate}',          '\\begin{enumerate}\n\t\\item $0\n\\end{enumerate}',                                            'Numbered list'),
          snip('\\begin{description}',        '\\begin{description}\n\t\\item[${1:term}] $0\n\\end{description}',                             'Description list'),
          kw  ('\\item',                      '\\item ',                                                                                       'List item'),
          snip('\\item[]',                    '\\item[$1] ',                                                                                   'Labeled list item'),

          // ── Figures & graphics ───────────────────────────────────────────
          snip('\\begin{figure}',             '\\begin{figure}[${1:htbp}]\n\t\\centering\n\t\\includegraphics[width=${2:0.8}\\textwidth]{${3:filename}}\n\t\\caption{${4:Caption}}\n\t\\label{fig:${5:label}}\n\\end{figure}', 'Figure environment'),
          snip('\\begin{figure*}',            '\\begin{figure*}[${1:t}]\n\t\\centering\n\t\\includegraphics[width=${2:\\textwidth}]{${3:filename}}\n\t\\caption{${4:Caption}}\n\t\\label{fig:${5:label}}\n\\end{figure*}', 'Double-column figure'),
          snip('\\includegraphics{}',         '\\includegraphics[width=${1:0.8}\\textwidth]{${2:filename}}',                                  'Include image'),
          snip('\\caption{}',                 '\\caption{$1}',                                                                                 'Float caption'),
          snip('\\captionof{}{}',             '\\captionof{${1:figure}}{$2}',                                                                  'Caption outside float (capt-of)'),
          snip('\\subcaption{}',              '\\subcaption{$1}',                                                                              'Subcaption (subcaption package)'),
          snip('\\subfigure',                 '\\subfigure[${1:caption}]{\\includegraphics[width=${2:0.45}\\textwidth]{${3:file}}}',           'Subfigure (subfigure package)'),
          snip('\\graphicspath{}',            '\\graphicspath{{${1:images/}}}',                                                               'Set graphics search path'),

          // ── Tables ───────────────────────────────────────────────────────
          snip('\\begin{table}',              '\\begin{table}[${1:htbp}]\n\t\\centering\n\t\\begin{tabular}{${2:lll}}\n\t\t\\hline\n\t\t${3:H1} & ${4:H2} & ${5:H3} \\\\\\\\\n\t\t\\hline\n\t\t${6:a} & ${7:b} & ${8:c} \\\\\\\\\n\t\t\\hline\n\t\\end{tabular}\n\t\\caption{${9:Caption}}\n\t\\label{tab:${10:label}}\n\\end{table}', 'Table environment'),
          snip('\\begin{tabular}',            '\\begin{tabular}{${1:l l}}\n\t$0\n\\end{tabular}',                                             'Tabular environment'),
          snip('\\begin{tabular*}',           '\\begin{tabular*}{${1:\\textwidth}}{${2:@{\\extracolsep{\\fill}}ll}}\n\t$0\n\\end{tabular*}',  'Fixed-width tabular'),
          snip('\\begin{tabularx}',           '\\begin{tabularx}{${1:\\textwidth}}{${2:XX}}\n\t$0\n\\end{tabularx}',                          'Tabularx environment'),
          snip('\\begin{longtable}',          '\\begin{longtable}{${1:lll}}\n\t\\hline\n\t$0\n\t\\hline\n\\end{longtable}',                  'Multi-page table'),
          snip('\\begin{booktabs}',           '\\toprule\n$1\n\\midrule\n$2\n\\bottomrule',                                                   'Booktabs rule snippet'),
          kw  ('\\hline',                     '\\hline',                                                                                       'Horizontal rule in table'),
          kw  ('\\toprule',                   '\\toprule',                                                                                     'Top rule (booktabs)'),
          kw  ('\\midrule',                   '\\midrule',                                                                                     'Middle rule (booktabs)'),
          kw  ('\\bottomrule',                '\\bottomrule',                                                                                  'Bottom rule (booktabs)'),
          snip('\\cmidrule{}',                '\\cmidrule{${1:2-3}}',                                                                          'Partial horizontal rule (booktabs)'),
          snip('\\multicolumn{}{}{}',         '\\multicolumn{${1:2}}{${2:c}}{${3:text}}',                                                      'Merge columns'),
          snip('\\multirow{}{}{}',            '\\multirow{${1:2}}{${2:*}}{${3:text}}',                                                         'Merge rows (multirow package)'),

          // ── Math environments ────────────────────────────────────────────
          snip('\\begin{equation}',           '\\begin{equation}\n\t$0\n\\end{equation}',                                                     'Numbered equation'),
          snip('\\begin{equation*}',          '\\begin{equation*}\n\t$0\n\\end{equation*}',                                                   'Unnumbered equation'),
          snip('\\begin{align}',              '\\begin{align}\n\t$0\n\\end{align}',                                                           'Aligned equations'),
          snip('\\begin{align*}',             '\\begin{align*}\n\t$0\n\\end{align*}',                                                         'Unnumbered aligned equations'),
          snip('\\begin{gather}',             '\\begin{gather}\n\t$0\n\\end{gather}',                                                         'Gathered equations'),
          snip('\\begin{gather*}',            '\\begin{gather*}\n\t$0\n\\end{gather*}',                                                       'Unnumbered gathered equations'),
          snip('\\begin{multline}',           '\\begin{multline}\n\t$0\n\\end{multline}',                                                     'Multi-line equation'),
          snip('\\begin{multline*}',          '\\begin{multline*}\n\t$0\n\\end{multline*}',                                                   'Unnumbered multi-line equation'),
          snip('\\begin{flalign}',            '\\begin{flalign}\n\t$0\n\\end{flalign}',                                                       'Full-width aligned equations'),
          snip('\\begin{flalign*}',           '\\begin{flalign*}\n\t$0\n\\end{flalign*}',                                                     'Unnumbered full-width aligned'),
          snip('\\begin{subequations}',       '\\begin{subequations}\n\t\\begin{align}\n\t\t$0\n\t\\end{align}\n\\end{subequations}',         'Subequations block'),
          snip('\\begin{matrix}',             '\\begin{matrix}\n\t$0\n\\end{matrix}',                                                         'Plain matrix'),
          snip('\\begin{bmatrix}',            '\\begin{bmatrix}\n\t$0\n\\end{bmatrix}',                                                       'Square-bracket matrix'),
          snip('\\begin{pmatrix}',            '\\begin{pmatrix}\n\t$0\n\\end{pmatrix}',                                                       'Round-bracket matrix'),
          snip('\\begin{vmatrix}',            '\\begin{vmatrix}\n\t$0\n\\end{vmatrix}',                                                       'Vertical-bar matrix (determinant)'),
          snip('\\begin{Vmatrix}',            '\\begin{Vmatrix}\n\t$0\n\\end{Vmatrix}',                                                       'Double vertical-bar matrix'),
          snip('\\begin{Bmatrix}',            '\\begin{Bmatrix}\n\t$0\n\\end{Bmatrix}',                                                       'Curly-brace matrix'),
          snip('\\begin{array}',              '\\begin{array}{${1:cc}}\n\t$0\n\\end{array}',                                                  'Array environment'),
          snip('\\begin{cases}',              '\\begin{cases}\n\t$1 & \\text{if } $2 \\\\\\\\\n\t$3 & \\text{otherwise}\n\\end{cases}',      'Piecewise / cases'),
          snip('\\begin{split}',              '\\begin{split}\n\t$0\n\\end{split}',                                                           'Split equation across lines'),

          // ── Math operators & symbols ─────────────────────────────────────
          snip('\\frac{}{}',                  '\\frac{$1}{$2}',                                                                               'Fraction'),
          snip('\\dfrac{}{}',                 '\\dfrac{$1}{$2}',                                                                              'Display-style fraction'),
          snip('\\tfrac{}{}',                 '\\tfrac{$1}{$2}',                                                                              'Text-style fraction'),
          snip('\\cfrac{}{}',                 '\\cfrac{$1}{$2}',                                                                              'Continued fraction'),
          snip('\\sqrt{}',                    '\\sqrt{$1}',                                                                                   'Square root'),
          snip('\\sqrt[]{}',                  '\\sqrt[${1:n}]{$2}',                                                                           'n-th root'),
          snip('\\sum',                       '\\sum_{${1:i=1}}^{${2:n}}',                                                                    'Summation'),
          snip('\\prod',                      '\\prod_{${1:i=1}}^{${2:n}}',                                                                   'Product'),
          snip('\\int',                       '\\int_{${1:a}}^{${2:b}}',                                                                      'Integral'),
          snip('\\iint',                      '\\iint_{$1}',                                                                                   'Double integral'),
          snip('\\iiint',                     '\\iiint_{$1}',                                                                                  'Triple integral'),
          snip('\\oint',                      '\\oint_{$1}',                                                                                   'Contour integral'),
          snip('\\lim',                       '\\lim_{${1:x \\to \\infty}}',                                                                  'Limit'),
          snip('\\limsup',                    '\\limsup_{${1:n \\to \\infty}}',                                                               'Limit superior'),
          snip('\\liminf',                    '\\liminf_{${1:n \\to \\infty}}',                                                               'Limit inferior'),
          snip('\\sup',                       '\\sup_{$1}',                                                                                    'Supremum'),
          snip('\\inf',                       '\\inf_{$1}',                                                                                    'Infimum'),
          snip('\\max',                       '\\max_{$1}',                                                                                    'Maximum'),
          snip('\\min',                       '\\min_{$1}',                                                                                    'Minimum'),
          snip('\\det',                       '\\det($1)',                                                                                     'Determinant'),
          snip('\\text{}',                    '\\text{$1}',                                                                                    'Text inside math mode'),
          snip('\\mathrm{}',                  '\\mathrm{$1}',                                                                                  'Roman font in math'),
          snip('\\mathbf{}',                  '\\mathbf{$1}',                                                                                  'Bold in math'),
          snip('\\mathbb{}',                  '\\mathbb{$1}',                                                                                  'Blackboard bold (amsfonts)'),
          snip('\\mathcal{}',                 '\\mathcal{$1}',                                                                                 'Calligraphic font in math'),
          snip('\\mathfrak{}',                '\\mathfrak{$1}',                                                                                'Fraktur font in math'),
          snip('\\mathit{}',                  '\\mathit{$1}',                                                                                  'Italic in math'),
          snip('\\boldsymbol{}',              '\\boldsymbol{$1}',                                                                              'Bold symbol (amsbsy)'),
          snip('\\bm{}',                      '\\bm{$1}',                                                                                      'Bold math (bm package)'),
          snip('\\operatorname{}',            '\\operatorname{$1}',                                                                            'Custom operator name'),
          snip('\\DeclareMathOperator{}{}',   '\\DeclareMathOperator{\\${1:op}}{${2:name}}',                                                   'Declare math operator'),
          snip('\\overset{}{}',               '\\overset{${1:above}}{${2:base}}',                                                             'Symbol above'),
          snip('\\underset{}{}',              '\\underset{${1:below}}{${2:base}}',                                                             'Symbol below'),
          snip('\\overbrace{}',               '\\overbrace{$1}^{${2:label}}',                                                                  'Overbrace'),
          snip('\\underbrace{}',              '\\underbrace{$1}_{${2:label}}',                                                                 'Underbrace'),
          snip('\\overline{}',                '\\overline{$1}',                                                                                'Overline'),
          snip('\\underline{}',               '\\underline{$1}',                                                                               'Underline'),
          snip('\\overrightarrow{}',          '\\overrightarrow{$1}',                                                                          'Vector arrow over'),
          snip('\\xleftarrow{}',              '\\xleftarrow{$1}',                                                                              'Extensible left arrow'),
          snip('\\xrightarrow{}',             '\\xrightarrow{$1}',                                                                             'Extensible right arrow'),
          snip('\\stackrel{}{}',              '\\stackrel{${1:above}}{${2:rel}}',                                                              'Stack symbols'),
          kw  ('\\partial',                   '\\partial',                                                                                     'Partial derivative symbol'),
          kw  ('\\nabla',                     '\\nabla',                                                                                       'Nabla / gradient operator'),
          kw  ('\\infty',                     '\\infty',                                                                                       'Infinity'),
          kw  ('\\hbar',                      '\\hbar',                                                                                       'Reduced Planck constant'),
          kw  ('\\ell',                       '\\ell',                                                                                         'Script l'),
          kw  ('\\Re',                        '\\Re',                                                                                          'Real part'),
          kw  ('\\Im',                        '\\Im',                                                                                          'Imaginary part'),
          kw  ('\\aleph',                     '\\aleph',                                                                                       'Aleph (set theory)'),
          kw  ('\\emptyset',                  '\\emptyset',                                                                                    'Empty set'),
          kw  ('\\varnothing',                '\\varnothing',                                                                                  'Empty set (variant)'),
          kw  ('\\cdots',                     '\\cdots',                                                                                       'Centered dots'),
          kw  ('\\ldots',                     '\\ldots',                                                                                       'Dots on baseline'),
          kw  ('\\vdots',                     '\\vdots',                                                                                       'Vertical dots'),
          kw  ('\\ddots',                     '\\ddots',                                                                                       'Diagonal dots'),
          kw  ('\\prime',                     '\\prime',                                                                                       'Prime symbol'),
          kw  ('\\circ',                      '\\circ',                                                                                        'Composition operator'),
          kw  ('\\bullet',                    '\\bullet',                                                                                      'Bullet'),
          kw  ('\\star',                      '\\star',                                                                                        'Star operator'),
          kw  ('\\dagger',                    '\\dagger',                                                                                      'Dagger'),
          kw  ('\\ddagger',                   '\\ddagger',                                                                                     'Double dagger'),
          kw  ('\\angle',                     '\\angle',                                                                                       'Angle symbol'),
          kw  ('\\perp',                      '\\perp',                                                                                        'Perpendicular'),
          kw  ('\\parallel',                  '\\parallel',                                                                                    'Parallel lines'),
          kw  ('\\triangle',                  '\\triangle',                                                                                    'Triangle symbol'),

          // ── Greek letters – lowercase ────────────────────────────────────
          kw  ('\\alpha',                     '\\alpha',                                                                                       'α – Greek letter alpha'),
          kw  ('\\beta',                      '\\beta',                                                                                        'β – Greek letter beta'),
          kw  ('\\gamma',                     '\\gamma',                                                                                       'γ – Greek letter gamma'),
          kw  ('\\delta',                     '\\delta',                                                                                       'δ – Greek letter delta'),
          kw  ('\\epsilon',                   '\\epsilon',                                                                                     'ε – Greek letter epsilon'),
          kw  ('\\varepsilon',                '\\varepsilon',                                                                                  'ε – Variant epsilon'),
          kw  ('\\zeta',                      '\\zeta',                                                                                        'ζ – Greek letter zeta'),
          kw  ('\\eta',                       '\\eta',                                                                                         'η – Greek letter eta'),
          kw  ('\\theta',                     '\\theta',                                                                                       'θ – Greek letter theta'),
          kw  ('\\vartheta',                  '\\vartheta',                                                                                    'ϑ – Variant theta'),
          kw  ('\\iota',                      '\\iota',                                                                                        'ι – Greek letter iota'),
          kw  ('\\kappa',                     '\\kappa',                                                                                       'κ – Greek letter kappa'),
          kw  ('\\lambda',                    '\\lambda',                                                                                      'λ – Greek letter lambda'),
          kw  ('\\mu',                        '\\mu',                                                                                          'μ – Greek letter mu'),
          kw  ('\\nu',                        '\\nu',                                                                                          'ν – Greek letter nu'),
          kw  ('\\xi',                        '\\xi',                                                                                          'ξ – Greek letter xi'),
          kw  ('\\pi',                        '\\pi',                                                                                          'π – Greek letter pi'),
          kw  ('\\varpi',                     '\\varpi',                                                                                       'ϖ – Variant pi'),
          kw  ('\\rho',                       '\\rho',                                                                                         'ρ – Greek letter rho'),
          kw  ('\\varrho',                    '\\varrho',                                                                                      'ϱ – Variant rho'),
          kw  ('\\sigma',                     '\\sigma',                                                                                       'σ – Greek letter sigma'),
          kw  ('\\varsigma',                  '\\varsigma',                                                                                    'ς – Variant sigma'),
          kw  ('\\tau',                       '\\tau',                                                                                         'τ – Greek letter tau'),
          kw  ('\\upsilon',                   '\\upsilon',                                                                                     'υ – Greek letter upsilon'),
          kw  ('\\phi',                       '\\phi',                                                                                         'φ – Greek letter phi'),
          kw  ('\\varphi',                    '\\varphi',                                                                                      'φ – Variant phi'),
          kw  ('\\chi',                       '\\chi',                                                                                         'χ – Greek letter chi'),
          kw  ('\\psi',                       '\\psi',                                                                                         'ψ – Greek letter psi'),
          kw  ('\\omega',                     '\\omega',                                                                                       'ω – Greek letter omega'),

          // ── Greek letters – uppercase ────────────────────────────────────
          kw  ('\\Gamma',                     '\\Gamma',                                                                                       'Γ – Greek capital Gamma'),
          kw  ('\\Delta',                     '\\Delta',                                                                                       'Δ – Greek capital Delta'),
          kw  ('\\Theta',                     '\\Theta',                                                                                       'Θ – Greek capital Theta'),
          kw  ('\\Lambda',                    '\\Lambda',                                                                                      'Λ – Greek capital Lambda'),
          kw  ('\\Xi',                        '\\Xi',                                                                                          'Ξ – Greek capital Xi'),
          kw  ('\\Pi',                        '\\Pi',                                                                                          'Π – Greek capital Pi'),
          kw  ('\\Sigma',                     '\\Sigma',                                                                                       'Σ – Greek capital Sigma'),
          kw  ('\\Upsilon',                   '\\Upsilon',                                                                                     'Υ – Greek capital Upsilon'),
          kw  ('\\Phi',                       '\\Phi',                                                                                         'Φ – Greek capital Phi'),
          kw  ('\\Psi',                       '\\Psi',                                                                                         'Ψ – Greek capital Psi'),
          kw  ('\\Omega',                     '\\Omega',                                                                                       'Ω – Greek capital Omega'),

          // ── Relations ────────────────────────────────────────────────────
          kw  ('\\leq',                       '\\leq',                                                                                         '≤ – Less than or equal'),
          kw  ('\\geq',                       '\\geq',                                                                                         '≥ – Greater than or equal'),
          kw  ('\\neq',                       '\\neq',                                                                                         '≠ – Not equal'),
          kw  ('\\approx',                    '\\approx',                                                                                      '≈ – Approximately equal'),
          kw  ('\\equiv',                     '\\equiv',                                                                                       '≡ – Equivalent / congruent'),
          kw  ('\\sim',                       '\\sim',                                                                                         '∼ – Similar'),
          kw  ('\\simeq',                     '\\simeq',                                                                                       '≃ – Similar or equal'),
          kw  ('\\cong',                      '\\cong',                                                                                        '≅ – Congruent'),
          kw  ('\\propto',                    '\\propto',                                                                                      '∝ – Proportional to'),
          kw  ('\\ll',                        '\\ll',                                                                                          '≪ – Much less than'),
          kw  ('\\gg',                        '\\gg',                                                                                          '≫ – Much greater than'),
          kw  ('\\leqslant',                  '\\leqslant',                                                                                    '⩽ – Less than or equal (slant)'),
          kw  ('\\geqslant',                  '\\geqslant',                                                                                    '⩾ – Greater than or equal (slant)'),
          kw  ('\\prec',                      '\\prec',                                                                                        '≺ – Precedes'),
          kw  ('\\succ',                      '\\succ',                                                                                        '≻ – Succeeds'),
          kw  ('\\preceq',                    '\\preceq',                                                                                      '⪯ – Precedes or equal'),
          kw  ('\\succeq',                    '\\succeq',                                                                                      '⪰ – Succeeds or equal'),

          // ── Set theory ───────────────────────────────────────────────────
          kw  ('\\in',                        '\\in',                                                                                          '∈ – Element of'),
          kw  ('\\notin',                     '\\notin',                                                                                       '∉ – Not element of'),
          kw  ('\\ni',                        '\\ni',                                                                                          '∋ – Contains as element'),
          kw  ('\\subset',                    '\\subset',                                                                                      '⊂ – Subset'),
          kw  ('\\supset',                    '\\supset',                                                                                      '⊃ – Superset'),
          kw  ('\\subseteq',                  '\\subseteq',                                                                                    '⊆ – Subset or equal'),
          kw  ('\\supseteq',                  '\\supseteq',                                                                                    '⊇ – Superset or equal'),
          kw  ('\\subsetneq',                 '\\subsetneq',                                                                                   '⊊ – Proper subset'),
          kw  ('\\cup',                       '\\cup',                                                                                         '∪ – Union'),
          kw  ('\\cap',                       '\\cap',                                                                                         '∩ – Intersection'),
          kw  ('\\setminus',                  '\\setminus',                                                                                    '∖ – Set minus'),
          kw  ('\\bigcup',                    '\\bigcup',                                                                                      '⋃ – Big union'),
          kw  ('\\bigcap',                    '\\bigcap',                                                                                      '⋂ – Big intersection'),
          kw  ('\\mathbb{R}',                 '\\mathbb{R}',                                                                                   'ℝ – Real numbers'),
          kw  ('\\mathbb{N}',                 '\\mathbb{N}',                                                                                   'ℕ – Natural numbers'),
          kw  ('\\mathbb{Z}',                 '\\mathbb{Z}',                                                                                   'ℤ – Integers'),
          kw  ('\\mathbb{Q}',                 '\\mathbb{Q}',                                                                                   'ℚ – Rational numbers'),
          kw  ('\\mathbb{C}',                 '\\mathbb{C}',                                                                                   'ℂ – Complex numbers'),

          // ── Logic ─────────────────────────────────────────────────────────
          kw  ('\\forall',                    '\\forall',                                                                                      '∀ – For all'),
          kw  ('\\exists',                    '\\exists',                                                                                      '∃ – There exists'),
          kw  ('\\nexists',                   '\\nexists',                                                                                     '∄ – Does not exist'),
          kw  ('\\neg',                       '\\neg',                                                                                         '¬ – Logical not'),
          kw  ('\\lnot',                      '\\lnot',                                                                                        '¬ – Logical not (alias)'),
          kw  ('\\land',                      '\\land',                                                                                        '∧ – Logical and'),
          kw  ('\\lor',                       '\\lor',                                                                                         '∨ – Logical or'),
          kw  ('\\Rightarrow',                '\\Rightarrow',                                                                                  '⇒ – Implies'),
          kw  ('\\Leftarrow',                 '\\Leftarrow',                                                                                   '⇐ – Implied by'),
          kw  ('\\Leftrightarrow',            '\\Leftrightarrow',                                                                              '⟺ – If and only if'),
          kw  ('\\iff',                       '\\iff',                                                                                         '⟺ – If and only if (spacing)'),
          kw  ('\\implies',                   '\\implies',                                                                                     '⟹ – Implies (wide)'),
          kw  ('\\top',                       '\\top',                                                                                         '⊤ – Tautology / top'),
          kw  ('\\bot',                       '\\bot',                                                                                         '⊥ – Contradiction / bottom'),
          kw  ('\\models',                    '\\models',                                                                                      '⊨ – Models / satisfies'),
          kw  ('\\vdash',                     '\\vdash',                                                                                       '⊢ – Proves / derives'),

          // ── Arrows ───────────────────────────────────────────────────────
          kw  ('\\rightarrow',                '\\rightarrow',                                                                                  '→ – Right arrow'),
          kw  ('\\leftarrow',                 '\\leftarrow',                                                                                   '← – Left arrow'),
          kw  ('\\leftrightarrow',            '\\leftrightarrow',                                                                              '↔ – Left-right arrow'),
          kw  ('\\longrightarrow',            '\\longrightarrow',                                                                              '⟶ – Long right arrow'),
          kw  ('\\longleftarrow',             '\\longleftarrow',                                                                               '⟵ – Long left arrow'),
          kw  ('\\Longrightarrow',            '\\Longrightarrow',                                                                              '⟹ – Long double right arrow'),
          kw  ('\\Longleftarrow',             '\\Longleftarrow',                                                                               '⟸ – Long double left arrow'),
          kw  ('\\uparrow',                   '\\uparrow',                                                                                     '↑ – Up arrow'),
          kw  ('\\downarrow',                 '\\downarrow',                                                                                   '↓ – Down arrow'),
          kw  ('\\updownarrow',               '\\updownarrow',                                                                                 '↕ – Up-down arrow'),
          kw  ('\\nearrow',                   '\\nearrow',                                                                                     '↗ – North-east arrow'),
          kw  ('\\searrow',                   '\\searrow',                                                                                     '↘ – South-east arrow'),
          kw  ('\\swarrow',                   '\\swarrow',                                                                                     '↙ – South-west arrow'),
          kw  ('\\nwarrow',                   '\\nwarrow',                                                                                     '↖ – North-west arrow'),
          kw  ('\\mapsto',                    '\\mapsto',                                                                                      '↦ – Maps to'),
          kw  ('\\hookrightarrow',            '\\hookrightarrow',                                                                              '↪ – Hook right arrow'),
          kw  ('\\hookleftarrow',             '\\hookleftarrow',                                                                               '↩ – Hook left arrow'),
          kw  ('\\to',                        '\\to',                                                                                          '→ – To (alias)'),
          kw  ('\\gets',                      '\\gets',                                                                                        '← – Gets (alias)'),

          // ── Binary operators ─────────────────────────────────────────────
          kw  ('\\times',                     '\\times',                                                                                       '× – Multiplication'),
          kw  ('\\div',                       '\\div',                                                                                         '÷ – Division'),
          kw  ('\\cdot',                      '\\cdot',                                                                                        '· – Centered dot'),
          kw  ('\\pm',                        '\\pm',                                                                                          '± – Plus or minus'),
          kw  ('\\mp',                        '\\mp',                                                                                          '∓ – Minus or plus'),
          kw  ('\\oplus',                     '\\oplus',                                                                                       '⊕ – Direct sum'),
          kw  ('\\ominus',                    '\\ominus',                                                                                      '⊖ – Circle minus'),
          kw  ('\\otimes',                    '\\otimes',                                                                                      '⊗ – Tensor product'),
          kw  ('\\oslash',                    '\\oslash',                                                                                      '⊘ – Circle slash'),
          kw  ('\\odot',                      '\\odot',                                                                                        '⊙ – Circle dot'),
          kw  ('\\wedge',                     '\\wedge',                                                                                       '∧ – Wedge product'),
          kw  ('\\vee',                       '\\vee',                                                                                         '∨ – Vee / join'),
          kw  ('\\sqcup',                     '\\sqcup',                                                                                       '⊔ – Square cup'),
          kw  ('\\sqcap',                     '\\sqcap',                                                                                       '⊓ – Square cap'),
          kw  ('\\amalg',                     '\\amalg',                                                                                       '⊔ – Amalgamation'),

          // ── Math accents / decorations ───────────────────────────────────
          snip('\\hat{}',                     '\\hat{$1}',                                                                                     'Hat accent'),
          snip('\\bar{}',                     '\\bar{$1}',                                                                                     'Bar accent'),
          snip('\\vec{}',                     '\\vec{$1}',                                                                                     'Vector arrow accent'),
          snip('\\tilde{}',                   '\\tilde{$1}',                                                                                   'Tilde accent'),
          snip('\\dot{}',                     '\\dot{$1}',                                                                                     'Dot accent'),
          snip('\\ddot{}',                    '\\ddot{$1}',                                                                                    'Double dot accent'),
          snip('\\acute{}',                   '\\acute{$1}',                                                                                   'Acute accent'),
          snip('\\grave{}',                   '\\grave{$1}',                                                                                   'Grave accent'),
          snip('\\check{}',                   '\\check{$1}',                                                                                   'Háček / check accent'),
          snip('\\breve{}',                   '\\breve{$1}',                                                                                   'Breve accent'),
          snip('\\widehat{}',                 '\\widehat{$1}',                                                                                 'Wide hat'),
          snip('\\widetilde{}',               '\\widetilde{$1}',                                                                               'Wide tilde'),

          // ── Delimiters ───────────────────────────────────────────────────
          snip('\\left(',                     '\\left( $1 \\right)',                                                                           'Auto-sized round brackets'),
          snip('\\left[',                     '\\left[ $1 \\right]',                                                                           'Auto-sized square brackets'),
          snip('\\left\\{',                   '\\left\\{ $1 \\right\\}',                                                                       'Auto-sized curly braces'),
          snip('\\left|',                     '\\left| $1 \\right|',                                                                           'Auto-sized absolute value bars'),
          snip('\\left\\|',                   '\\left\\| $1 \\right\\|',                                                                       'Auto-sized norm double bars'),
          snip('\\left\\langle',              '\\left\\langle $1 \\right\\rangle',                                                            'Auto-sized angle brackets'),
          snip('\\left\\lfloor',              '\\left\\lfloor $1 \\right\\rfloor',                                                            'Auto-sized floor brackets'),
          snip('\\left\\lceil',               '\\left\\lceil $1 \\right\\rceil',                                                              'Auto-sized ceiling brackets'),
          kw  ('\\langle',                    '\\langle',                                                                                      '⟨ – Left angle bracket'),
          kw  ('\\rangle',                    '\\rangle',                                                                                      '⟩ – Right angle bracket'),
          kw  ('\\lfloor',                    '\\lfloor',                                                                                      '⌊ – Left floor'),
          kw  ('\\rfloor',                    '\\rfloor',                                                                                      '⌋ – Right floor'),
          kw  ('\\lceil',                     '\\lceil',                                                                                       '⌈ – Left ceiling'),
          kw  ('\\rceil',                     '\\rceil',                                                                                       '⌉ – Right ceiling'),

          // ── Spacing ───────────────────────────────────────────────────────
          snip('\\vspace{}',                  '\\vspace{$1}',                                                                                  'Vertical space'),
          snip('\\vspace*{}',                 '\\vspace*{$1}',                                                                                 'Vertical space (forced)'),
          snip('\\hspace{}',                  '\\hspace{$1}',                                                                                  'Horizontal space'),
          snip('\\hspace*{}',                 '\\hspace*{$1}',                                                                                 'Horizontal space (forced)'),
          kw  ('\\smallskip',                 '\\smallskip',                                                                                   'Small vertical skip'),
          kw  ('\\medskip',                   '\\medskip',                                                                                     'Medium vertical skip'),
          kw  ('\\bigskip',                   '\\bigskip',                                                                                     'Big vertical skip'),
          kw  ('\\hfill',                     '\\hfill',                                                                                       'Horizontal fill'),
          kw  ('\\vfill',                     '\\vfill',                                                                                       'Vertical fill'),
          kw  ('\\newpage',                   '\\newpage',                                                                                     'Force new page'),
          kw  ('\\clearpage',                 '\\clearpage',                                                                                   'Output floats and new page'),
          kw  ('\\cleardoublepage',           '\\cleardoublepage',                                                                             'Clear to odd page'),
          kw  ('\\pagebreak',                 '\\pagebreak',                                                                                   'Page break hint'),
          kw  ('\\linebreak',                 '\\linebreak',                                                                                   'Line break hint'),
          kw  ('\\newline',                   '\\newline',                                                                                     'New line (text mode)'),
          kw  ('\\noindent',                  '\\noindent',                                                                                    'Suppress paragraph indent'),
          kw  ('\\centering',                 '\\centering',                                                                                   'Center content in scope'),
          kw  ('\\raggedright',               '\\raggedright',                                                                                 'Ragged right alignment'),
          kw  ('\\raggedleft',                '\\raggedleft',                                                                                  'Ragged left alignment'),
          snip('\\begin{center}',             '\\begin{center}\n\t$0\n\\end{center}',                                                         'Center environment'),
          snip('\\begin{flushleft}',          '\\begin{flushleft}\n\t$0\n\\end{flushleft}',                                                   'Flush left environment'),
          snip('\\begin{flushright}',         '\\begin{flushright}\n\t$0\n\\end{flushright}',                                                 'Flush right environment'),

          // ── References & labels ──────────────────────────────────────────
          snip('\\label{}',                   '\\label{$1}',                                                                                   'Define label'),
          snip('\\ref{}',                     '\\ref{$1}',                                                                                     'Reference label'),
          snip('\\eqref{}',                   '\\eqref{$1}',                                                                                   'Equation reference (amsmath)'),
          snip('\\pageref{}',                 '\\pageref{$1}',                                                                                 'Page reference'),
          snip('\\cite{}',                    '\\cite{$1}',                                                                                    'Citation'),
          snip('\\cite[]{}',                  '\\cite[${1:p.~10}]{$2}',                                                                        'Citation with note'),
          snip('\\nocite{}',                  '\\nocite{$1}',                                                                                  'Include in bibliography without citing'),

          // ── Bibliography ─────────────────────────────────────────────────
          snip('\\bibliography{}',            '\\bibliography{$1}',                                                                            'Bibliography file'),
          snip('\\bibliographystyle{}',       '\\bibliographystyle{$1}',                                                                       'Bibliography style'),
          snip('\\bibitem{}',                 '\\bibitem{$1}',                                                                                  'Bibliography entry'),
          snip('\\begin{thebibliography}',    '\\begin{thebibliography}{${1:99}}\n\t\\bibitem{$2} $0\n\\end{thebibliography}',                 'Inline bibliography'),

          // ── natbib ───────────────────────────────────────────────────────
          snip('\\citep{}',                   '\\citep{$1}',                                                                                   'Parenthetical citation (natbib)'),
          snip('\\citet{}',                   '\\citet{$1}',                                                                                   'Textual citation (natbib)'),
          snip('\\citep[][]{}',               '\\citep[${1:pre}][${2:post}]{$3}',                                                             'Parenthetical citation with notes'),
          snip('\\citet[]{}',                 '\\citet[${1:note}]{$2}',                                                                        'Textual citation with note'),
          snip('\\citealt{}',                 '\\citealt{$1}',                                                                                 'Citation without brackets (natbib)'),
          snip('\\citealp{}',                 '\\citealp{$1}',                                                                                 'Citation without brackets parenthetical (natbib)'),
          snip('\\citeauthor{}',              '\\citeauthor{$1}',                                                                              'Cite author only (natbib)'),
          snip('\\citeyear{}',                '\\citeyear{$1}',                                                                                'Cite year only (natbib)'),
          snip('\\citeyearpar{}',             '\\citeyearpar{$1}',                                                                             'Cite year in parens (natbib)'),

          // ── hyperref ─────────────────────────────────────────────────────
          snip('\\href{}{}',                  '\\href{${1:url}}{${2:text}}',                                                                   'Hyperlink (hyperref)'),
          snip('\\url{}',                     '\\url{$1}',                                                                                     'Typeset URL (hyperref)'),
          snip('\\hyperref[]{}',              '\\hyperref[${1:label}]{${2:text}}',                                                             'Internal hyperlink (hyperref)'),
          snip('\\autoref{}',                 '\\autoref{$1}',                                                                                 'Auto-named reference (hyperref)'),
          snip('\\nameref{}',                 '\\nameref{$1}',                                                                                 'Reference by name (hyperref)'),
          snip('\\hypersetup{}',              '\\hypersetup{\n\tcolorlinks=true,\n\tlinkcolor=${1:blue},\n\tcitecolor=${2:green},\n\turlcolor=${3:cyan}\n}', 'Configure hyperref'),

          // ── Theorem-like environments (amsthm) ───────────────────────────
          snip('\\begin{theorem}',            '\\begin{theorem}\n\t$0\n\\end{theorem}',                                                        'Theorem environment'),
          snip('\\begin{lemma}',              '\\begin{lemma}\n\t$0\n\\end{lemma}',                                                            'Lemma environment'),
          snip('\\begin{corollary}',          '\\begin{corollary}\n\t$0\n\\end{corollary}',                                                    'Corollary environment'),
          snip('\\begin{proposition}',        '\\begin{proposition}\n\t$0\n\\end{proposition}',                                                'Proposition environment'),
          snip('\\begin{definition}',         '\\begin{definition}\n\t$0\n\\end{definition}',                                                  'Definition environment'),
          snip('\\begin{remark}',             '\\begin{remark}\n\t$0\n\\end{remark}',                                                          'Remark environment'),
          snip('\\begin{example}',            '\\begin{example}\n\t$0\n\\end{example}',                                                        'Example environment'),
          snip('\\begin{conjecture}',         '\\begin{conjecture}\n\t$0\n\\end{conjecture}',                                                  'Conjecture environment'),
          snip('\\begin{proof}',              '\\begin{proof}\n\t$0\n\\end{proof}',                                                            'Proof environment'),
          snip('\\begin{proof}[]',            '\\begin{proof}[${1:Proof of \\Cref{$2}}]\n\t$0\n\\end{proof}',                                 'Named proof'),
          snip('\\newtheorem{}{}',            '\\newtheorem{${1:thm}}{${2:Theorem}}',                                                          'Declare theorem-like env'),
          snip('\\newtheorem*{}{}',           '\\newtheorem*{${1:thm}}{${2:Theorem}}',                                                         'Declare unnumbered theorem env'),
          kw  ('\\qed',                       '\\qed',                                                                                          'QED marker (amsthm)'),
          kw  ('\\qquad',                     '\\qquad',                                                                                       'Double quad space'),
          kw  ('\\quad',                      '\\quad',                                                                                        'Quad space'),

          // ── Algorithm environments (algorithm2e / algorithmicx) ───────────
          snip('\\begin{algorithm}',          '\\begin{algorithm}[${1:H}]\n\t\\caption{${2:Algorithm}}\n\t\\label{alg:${3:label}}\n\t\\begin{algorithmic}[1]\n\t\t$0\n\t\\end{algorithmic}\n\\end{algorithm}', 'Algorithm float (algorithm + algorithmicx)'),
          snip('\\begin{algorithmic}',        '\\begin{algorithmic}[1]\n\t$0\n\\end{algorithmic}',                                             'Algorithmic body'),
          snip('\\State',                     '\\State $1',                                                                                    'Statement (algorithmicx)'),
          snip('\\If{}',                      '\\If{$1}\n\t$0\n\\EndIf',                                                                       'If block (algorithmicx)'),
          snip('\\If{}\\Else{}',              '\\If{$1}\n\t$2\n\\Else\n\t$3\n\\EndIf',                                                         'If-Else block'),
          snip('\\For{}',                     '\\For{$1}\n\t$0\n\\EndFor',                                                                     'For loop (algorithmicx)'),
          snip('\\ForAll{}',                  '\\ForAll{$1}\n\t$0\n\\EndFor',                                                                  'For-all loop'),
          snip('\\While{}',                   '\\While{$1}\n\t$0\n\\EndWhile',                                                                 'While loop (algorithmicx)'),
          snip('\\Function{}{}',              '\\Function{${1:name}}{${2:params}}\n\t$0\n\\EndFunction',                                       'Function block (algorithmicx)'),
          snip('\\Procedure{}{}',             '\\Procedure{${1:name}}{${2:params}}\n\t$0\n\\EndProcedure',                                     'Procedure block (algorithmicx)'),
          kw  ('\\Return',                    '\\Return',                                                                                       'Return statement (algorithmicx)'),
          kw  ('\\Require',                   '\\Require',                                                                                     'Require / precondition'),
          kw  ('\\Ensure',                    '\\Ensure',                                                                                      'Ensure / postcondition'),
          kw  ('\\Comment{}',                 '\\Comment{$1}',                                                                                  'Inline comment in algorithm'),

          // ── Beamer ───────────────────────────────────────────────────────
          snip('\\begin{frame}',              '\\begin{frame}{${1:Title}}\n\t$0\n\\end{frame}',                                               'Beamer frame'),
          snip('\\begin{frame}[]',            '\\begin{frame}[${1:t}]{${2:Title}}{${3:Subtitle}}\n\t$0\n\\end{frame}',                        'Beamer frame with options'),
          snip('\\begin{columns}',            '\\begin{columns}[${1:T}]\n\t\\begin{column}{${2:0.5}\\textwidth}\n\t\t$0\n\t\\end{column}\n\t\\begin{column}{${3:0.5}\\textwidth}\n\t\t\n\t\\end{column}\n\\end{columns}', 'Two-column beamer layout'),
          snip('\\begin{column}',             '\\begin{column}{${1:0.5}\\textwidth}\n\t$0\n\\end{column}',                                    'Beamer column'),
          snip('\\begin{block}',              '\\begin{block}{${1:Title}}\n\t$0\n\\end{block}',                                               'Beamer block'),
          snip('\\begin{alertblock}',         '\\begin{alertblock}{${1:Title}}\n\t$0\n\\end{alertblock}',                                     'Beamer alert block'),
          snip('\\begin{exampleblock}',       '\\begin{exampleblock}{${1:Title}}\n\t$0\n\\end{exampleblock}',                                 'Beamer example block'),
          snip('\\begin{overlayarea}',        '\\begin{overlayarea}{${1:\\textwidth}}{${2:4cm}}\n\t$0\n\\end{overlayarea}',                   'Beamer fixed-size overlay area'),
          snip('\\usetheme{}',                '\\usetheme{${1:Madrid}}',                                                                       'Beamer theme'),
          snip('\\usecolortheme{}',           '\\usecolortheme{${1:default}}',                                                                 'Beamer color theme'),
          snip('\\usefonttheme{}',            '\\usefonttheme{${1:serif}}',                                                                    'Beamer font theme'),
          snip('\\frametitle{}',              '\\frametitle{$1}',                                                                              'Frame title'),
          snip('\\framesubtitle{}',           '\\framesubtitle{$1}',                                                                           'Frame subtitle'),
          kw  ('\\pause',                     '\\pause',                                                                                        'Beamer pause / overlay'),
          snip('\\only<>{}',                  '\\only<${1:2}>{$2}',                                                                            'Show only on slide N'),
          snip('\\uncover<>{}',               '\\uncover<${1:2-}>{$2}',                                                                        'Uncover from slide N'),
          snip('\\visible<>{}',               '\\visible<${1:2-}>{$2}',                                                                        'Visible from slide N'),
          snip('\\alert<>{}',                 '\\alert<${1:1}>{$2}',                                                                           'Alert on slide N'),
          snip('\\alert{}',                   '\\alert{$1}',                                                                                   'Alert text (Beamer)'),

          // ── geometry package ─────────────────────────────────────────────
          snip('\\usepackage{geometry}',      '\\usepackage[${1:margin=1in}]{geometry}',                                                       'Load geometry package'),
          snip('\\geometry{}',                '\\geometry{${1:top=2cm, bottom=2cm, left=2.5cm, right=2.5cm}}',                                 'Set page geometry'),
          snip('\\newgeometry{}',             '\\newgeometry{${1:margin=1in}}',                                                                'Temporarily change geometry'),
          kw  ('\\restoregeometry',           '\\restoregeometry',                                                                             'Restore original geometry'),
          kw  ('\\textheight',                '\\textheight',                                                                                  'Text height dimension'),
          kw  ('\\textwidth',                 '\\textwidth',                                                                                   'Text width dimension'),
          kw  ('\\linewidth',                 '\\linewidth',                                                                                   'Current line width'),
          kw  ('\\columnwidth',               '\\columnwidth',                                                                                 'Column width'),
          kw  ('\\paperwidth',                '\\paperwidth',                                                                                   'Paper width'),
          kw  ('\\paperheight',               '\\paperheight',                                                                                  'Paper height'),

          // ── Color (xcolor / color) ────────────────────────────────────────
          snip('\\textcolor{}{}',             '\\textcolor{${1:red}}{$2}',                                                                     'Colored text (xcolor)'),
          snip('\\colorbox{}{}',              '\\colorbox{${1:yellow}}{$2}',                                                                   'Colored box background'),
          snip('\\fcolorbox{}{}{}',           '\\fcolorbox{${1:black}}{${2:yellow}}{$3}',                                                      'Framed colored box'),
          snip('\\color{}',                   '\\color{${1:red}}',                                                                             'Switch color (scope)'),
          snip('\\definecolor{}{}{}',         '\\definecolor{${1:mycolor}}{${2:RGB}}{${3:255,0,0}}',                                           'Define custom color'),
          snip('\\pagecolor{}',               '\\pagecolor{${1:white}}',                                                                       'Set page background color'),
          snip('\\rowcolor{}',                '\\rowcolor{${1:gray!20}}',                                                                      'Table row color (colortbl)'),
          snip('\\cellcolor{}',               '\\cellcolor{${1:gray!20}}',                                                                     'Table cell color (colortbl)'),

          // ── TikZ basics ───────────────────────────────────────────────────
          snip('\\begin{tikzpicture}',        '\\begin{tikzpicture}[${1:scale=1}]\n\t$0\n\\end{tikzpicture}',                                 'TikZ picture environment'),
          snip('\\draw',                      '\\draw[${1:->}] ($2) -- ($3);',                                                                 'TikZ draw command'),
          snip('\\draw circle',               '\\draw ($1) circle [radius=${2:1cm}];',                                                         'TikZ draw circle'),
          snip('\\draw rectangle',            '\\draw ($1) rectangle ($2);',                                                                   'TikZ draw rectangle'),
          snip('\\node',                      '\\node[${1:draw}] (${2:name}) at ($3) {$4};',                                                   'TikZ node'),
          snip('\\path',                      '\\path ($1) -- ($2);',                                                                           'TikZ path'),
          snip('\\coordinate',                '\\coordinate (${1:name}) at ($2);',                                                             'TikZ coordinate'),
          snip('\\fill',                      '\\fill[${1:blue}] ($2) circle [radius=${3:2pt}];',                                              'TikZ fill'),
          snip('\\filldraw',                  '\\filldraw[fill=${1:blue!20}, draw=${2:blue}] ($3) -- ($4) -- cycle;',                          'TikZ fill and draw'),
          snip('\\foreach',                   '\\foreach \\${1:i} in {${2:1,...,5}} {\n\t$0\n}',                                              'TikZ foreach loop'),
          snip('\\usetikzlibrary{}',          '\\usetikzlibrary{${1:arrows.meta,positioning}}',                                               'Load TikZ library'),
          snip('\\begin{scope}',              '\\begin{scope}[${1:shift={(1,0)}}]\n\t$0\n\\end{scope}',                                       'TikZ scope'),
          snip('\\tikzset{}',                 '\\tikzset{${1:every node/.style={draw}}}',                                                      'TikZ global settings'),

          // ── More environments ─────────────────────────────────────────────
          snip('\\begin{abstract}',           '\\begin{abstract}\n\t$0\n\\end{abstract}',                                                     'Abstract environment'),
          snip('\\begin{verbatim}',           '\\begin{verbatim}\n$0\n\\end{verbatim}',                                                       'Verbatim text'),
          snip('\\begin{lstlisting}',         '\\begin{lstlisting}[language=${1:Python},caption={${2:Caption}}]\n$0\n\\end{lstlisting}',       'Code listing (listings)'),
          snip('\\begin{minted}',             '\\begin{minted}{${1:python}}\n$0\n\\end{minted}',                                              'Code listing (minted)'),
          snip('\\begin{minipage}',           '\\begin{minipage}{${1:0.5}\\textwidth}\n\t$0\n\\end{minipage}',                                'Minipage'),
          snip('\\begin{multicols}',          '\\begin{multicols}{${1:2}}\n\t$0\n\\end{multicols}',                                           'Multi-column text (multicol)'),
          snip('\\begin{quote}',              '\\begin{quote}\n\t$0\n\\end{quote}',                                                           'Block quote'),
          snip('\\begin{quotation}',          '\\begin{quotation}\n\t$0\n\\end{quotation}',                                                   'Block quotation (indented)'),
          snip('\\begin{verse}',              '\\begin{verse}\n\t$0\n\\end{verse}',                                                           'Verse environment'),
          snip('\\begin{titlepage}',          '\\begin{titlepage}\n\t$0\n\\end{titlepage}',                                                   'Custom title page'),
          snip('\\begin{appendices}',         '\\begin{appendices}\n\t$0\n\\end{appendices}',                                                 'Appendices (appendix pkg)'),

          // ── Miscellaneous ─────────────────────────────────────────────────
          kw  ('\\today',                     '\\today',                                                                                        'Current date'),
          kw  ('\\LaTeX',                     '\\LaTeX',                                                                                        'LaTeX logo'),
          kw  ('\\TeX',                       '\\TeX',                                                                                          'TeX logo'),
          kw  ('\\BibTeX',                    '\\BibTeX',                                                                                       'BibTeX logo'),
          kw  ('\\linewidth',                 '\\linewidth',                                                                                   'Current line width'),
          kw  ('\\baselineskip',              '\\baselineskip',                                                                                'Current baseline skip'),
          snip('\\setlength{}{}',             '\\setlength{\\${1:parindent}}{${2:0pt}}',                                                       'Set length'),
          snip('\\addtolength{}{}',           '\\addtolength{\\${1:textwidth}}{${2:1cm}}',                                                     'Add to length'),
          snip('\\parbox{}{}',                '\\parbox{${1:0.5\\textwidth}}{$2}',                                                             'Paragraph box'),
          snip('\\raisebox{}{}',              '\\raisebox{${1:2pt}}{$2}',                                                                      'Raise box vertically'),
          snip('\\makebox{}{}',               '\\makebox[${1:2cm}][${2:c}]{$3}',                                                              'Make box with fixed width'),
          snip('\\framebox{}{}',              '\\framebox[${1:2cm}][${2:c}]{$3}',                                                             'Framed box with fixed width'),
          snip('\\rule{}{}',                  '\\rule{${1:\\linewidth}}{${2:0.4pt}}',                                                          'Horizontal rule'),
          snip('\\hrulefill',                 '\\hrulefill',                                                                                   'Horizontal rule fill'),
          kw  ('\\dotfill',                   '\\dotfill',                                                                                     'Dotted fill'),
          snip('\\addcontentsline{}{}{}',     '\\addcontentsline{${1:toc}}{${2:section}}{${3:Title}}',                                         'Add entry to TOC'),
          snip('\\markright{}',               '\\markright{$1}',                                                                               'Set right mark (running head)'),
          snip('\\markboth{}{}',              '\\markboth{${1:left}}{${2:right}}',                                                             'Set both marks'),
          snip('\\pagenumbering{}',           '\\pagenumbering{${1:arabic}}',                                                                  'Set page numbering style'),
          snip('\\setcounter{}{}',            '\\setcounter{${1:page}}{${2:1}}',                                                               'Set counter value'),
          snip('\\stepcounter{}',             '\\stepcounter{$1}',                                                                             'Step counter'),
          snip('\\refstepcounter{}',          '\\refstepcounter{$1}',                                                                           'Step counter with label support'),
          snip('\\newcounter{}',              '\\newcounter{$1}',                                                                              'Declare new counter'),
          snip('\\thepage',                   '\\thepage',                                                                                     'Current page number'),
          snip('\\thesection',                '\\thesection',                                                                                  'Current section number'),
          kw  ('\\nonumber',                  '\\nonumber',                                                                                    'Suppress equation number'),
          snip('\\tag{}',                     '\\tag{$1}',                                                                                     'Custom equation tag'),
          snip('\\intertext{}',               '\\intertext{$1}',                                                                               'Interleave text in align'),
          snip('\\shortintertext{}',          '\\shortintertext{$1}',                                                                          'Short interleaved text (mathtools)'),
          kw  ('\\blacksquare',               '\\blacksquare',                                                                                 '■ – Black square (QED)'),
          kw  ('\\square',                    '\\square',                                                                                       '□ – Open square'),
          kw  ('\\diamond',                   '\\diamond',                                                                                      '◇ – Diamond symbol'),
          snip('\\phantom{}',                 '\\phantom{$1}',                                                                                 'Invisible placeholder same size'),
          snip('\\hphantom{}',                '\\hphantom{$1}',                                                                                'Horizontal phantom'),
          snip('\\vphantom{}',                '\\vphantom{$1}',                                                                                'Vertical phantom'),
          snip('\\smash{}',                   '\\smash{$1}',                                                                                   'Remove height from box'),
          snip('\\ensuremath{}',              '\\ensuremath{$1}',                                                                              'Ensure math mode'),
          snip('\\SI{}{}',                    '\\SI{${1:9.8}}{${2:\\metre\\per\\second\\squared}}',                                            'SI unit (siunitx)'),
          snip('\\si{}',                      '\\si{${1:\\kilogram}}',                                                                          'SI unit only (siunitx)'),
          snip('\\num{}',                     '\\num{${1:1.23e4}}',                                                                            'Formatted number (siunitx)'),
          snip('\\cref{}',                    '\\cref{$1}',                                                                                    'Smart reference (cleveref)'),
          snip('\\Cref{}',                    '\\Cref{$1}',                                                                                    'Capitalized smart reference (cleveref)'),
          snip('\\enumerate', '\\begin{enumerate}\n\t\\item $1\n\\end{enumerate}', 'Enumerated list'),
          snip('\\itemize', '\\begin{itemize}\n\t\\item $1\n\\end{itemize}', 'Itemize list'),
          snip('\\equation', '\\begin{equation}\n\t$1\n\\end{equation}', 'Equation'),
          snip('\\align', '\\begin{align}\n\t$1\n\\end{align}', 'Align'),

        ];
        return { suggestions };
      },
    });

    // ✅ Auto-close \begin{env} - checks line above after Enter is pressed
    editor.onDidChangeModelContent((e) => {
      const model = editor.getModel();
      const position = editor.getPosition();
      if (!model || !position) return;

      // Only trigger when a newline was just inserted
      const change = e.changes[0];
      if (!change || !change.text.includes('\n')) return;

      // Check the line ABOVE current cursor (where \begin{} was typed)
      const prevLineNum = position.lineNumber - 1;
      if (prevLineNum < 1) return;
      const prevLine = model.getLineContent(prevLineNum);
      const match = prevLine.trim().match(/\\begin\{([^}]+)\}$/);
      if (!match) return;

      const env = match[1];
      // Don't double-close if \end already exists on next line
      if (position.lineNumber <= model.getLineCount()) {
        const nextLine = model.getLineContent(position.lineNumber);
        if (nextLine.includes(`\\end{${env}}`)) return;
      }

      const indent = prevLine.match(/^(\s*)/)[1];
      const itemEnvs = ['itemize', 'enumerate', 'description'];
      const inner = itemEnvs.includes(env) ? `${indent}\t\\item ` : `${indent}\t`;
      const closing = `\n${indent}\\end{${env}}`;

      // Insert \item and \end{} after cursor
      const curCol = position.column;
      editor.executeEdits('auto-close', [{
        range: {
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: model.getLineLength(position.lineNumber) + 1,
        },
        text: inner + closing,
      }]);

      // Place cursor at end of \item line
      editor.setPosition({
        lineNumber: position.lineNumber,
        column: inner.length + 1,
      });
    });
  };

  // ==================== RENDER ====================

  const theme = {
    bg: isDarkTheme ? '#1e1e2e' : '#ffffff',
    sidebar: isDarkTheme ? '#252535' : '#f8fafc',
    text: isDarkTheme ? '#cdd6f4' : '#1e293b',
    mutedText: isDarkTheme ? '#8899aa' : '#64748b',
    border: isDarkTheme ? '#3c3c5c' : '#e2e8f0',
    headerBg: isDarkTheme ? '#252535' : '#f8fafc',
    toolbarBg: isDarkTheme ? '#1a1a2e' : '#ffffff',
  };
  const slateBtn = {
    padding: '6px 12px',
    background: '#ffffff',
    color: '#475569',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    height: '34px',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: theme.bg
    }}>
      {/* Toolbar */}
      <div style={{
        padding: '6px 15px',
        background: theme.toolbarBg,
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.border}`,
        flexWrap: 'wrap'
      }}>
        <input type="file" ref={zipInputRef} onChange={handleZipUpload} accept=".zip" style={{ display: 'none' }} />
        <input type="file" ref={imageInputRef} onChange={handleImageUpload} accept="image/*,.pdf" style={{ display: 'none' }} />

        <button onClick={() => setShowTemplates(!showTemplates)} style={slateBtn}>
          Templates
        </button>

        <button
          onClick={() => {
            if (!user) { setShowAuthModal(true); setError('Please login to view projects'); }
            else { fetchProjects(); setShowProjectsModal(true); }
          }}
          style={slateBtn}
        >
          My Projects
        </button>

        <button
          onClick={() => {
            if (!user) { setShowAuthModal(true); setError('Please login to save projects'); }
            else { setShowSaveModal(true); }
          }}
          disabled={!latexCode}
          style={{...slateBtn, opacity: !latexCode ? 0.5 : 1, cursor: !latexCode ? 'not-allowed' : 'pointer'}}
        >
          Save Project
        </button>

        <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => { setShowUploadDropdown(v => !v); setShowExportDropdown(false); }}
            disabled={!user}
            style={{...slateBtn, opacity: !user ? 0.5 : 1, cursor: !user ? 'not-allowed' : 'pointer'}}
          >
            Upload ▾
          </button>
          {showUploadDropdown && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0,
              background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 200, minWidth: '150px'
            }}>
              <div
                onClick={() => { if (!isUploadingZip) { zipInputRef.current?.click(); setShowUploadDropdown(false); } }}
                style={{ padding: '10px 14px', cursor: isUploadingZip ? 'not-allowed' : 'pointer', fontSize: '14px', color: isUploadingZip ? '#94a3b8' : '#334155', borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => { if (!isUploadingZip) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                {isUploadingZip ? 'Uploading...' : 'Upload ZIP'}
              </div>
              <div
                onClick={() => { if (!isUploading) { imageInputRef.current?.click(); setShowUploadDropdown(false); } }}
                style={{ padding: '10px 14px', cursor: isUploading ? 'not-allowed' : 'pointer', fontSize: '14px', color: isUploading ? '#94a3b8' : '#334155' }}
                onMouseEnter={e => { if (!isUploading) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                {isUploading ? 'Uploading...' : 'Upload Image'}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={compilePDF}
          disabled={isCompiling || !latexCode}
          style={{
            padding: '6px 16px',
            background: isCompiling || !latexCode ? '#94a3b8' : '#16a34a',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isCompiling || !latexCode ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            height: '34px',
            whiteSpace: 'nowrap'
          }}
        >
          {isCompiling ? 'Compiling...' : 'Compile PDF'}
        </button>

        <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => { setShowExportDropdown(v => !v); setShowUploadDropdown(false); }}
            style={slateBtn}
          >
            Export ▾
          </button>
          {showExportDropdown && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0,
              background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 200, minWidth: '160px'
            }}>
              <div
                onClick={() => { if (pdfUrl) { downloadPDF(); setShowExportDropdown(false); } }}
                style={{ padding: '10px 14px', cursor: pdfUrl ? 'pointer' : 'not-allowed', fontSize: '14px', color: pdfUrl ? '#334155' : '#94a3b8', borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => { if (pdfUrl) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                Download PDF
              </div>
              <div
                onClick={() => { if (latexCode) { downloadTeX(); setShowExportDropdown(false); } }}
                style={{ padding: '10px 14px', cursor: latexCode ? 'pointer' : 'not-allowed', fontSize: '14px', color: latexCode ? '#334155' : '#94a3b8', borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => { if (latexCode) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                Download .tex
              </div>
              {currentProjectId && (
                <div
                  onClick={() => { downloadZIP(); setShowExportDropdown(false); }}
                  style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', color: '#334155', borderBottom: currentProjectId && user ? '1px solid #f1f5f9' : 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  Download ZIP
                </div>
              )}
              {currentProjectId && user && (
                <div
                  onClick={async () => {
                    setShowExportDropdown(false);
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_BASE}/api/projects/${currentProjectId}/share`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                      });
                      const data = await res.json();
                      if (data.shareLink) {
                        navigator.clipboard.writeText(data.shareLink);
                        showToast('🔗 Share link copied to clipboard!', 'success');
                      }
                    } catch(err) {
                      showToast('Failed to generate share link', 'error');
                    }
                  }}
                  style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', color: '#334155' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  Share
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedback && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
          <div style={{background: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px'}}>
            <h2 style={{marginBottom: '20px', color: '#1e293b'}}>📢 Send Feedback</h2>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Tell us what you like, what's missing, or report bugs..."
              style={{width: '100%', minHeight: '150px', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical'}}
            />
            <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
              <button onClick={sendFeedback} disabled={isSendingFeedback} style={{flex: 1, padding: '12px', background: '#5a6fd6', color: 'white', border: 'none', borderRadius: '8px', cursor: isSendingFeedback ? 'not-allowed' : 'pointer', fontWeight: 'bold'}}>
                {isSendingFeedback ? '📤 Sending...' : '📤 Send Feedback'}
              </button>
              <button onClick={() => {if (onFeedbackClose) onFeedbackClose(); setFeedbackText('');}} style={{padding: '12px 20px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer'}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ZIP Project Name Modal */}
      {showZipProjectModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
          <div style={{background: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '400px'}}>
            <h2 style={{marginBottom: '20px', color: '#1e293b'}}>📦 Create Project from ZIP</h2>
            <input
              type="text"
              value={zipProjectName}
              onChange={(e) => setZipProjectName(e.target.value)}
              placeholder="Enter project name..."
              onKeyPress={(e) => e.key === 'Enter' && handleZipProjectSave()}
              style={{width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', marginBottom: '20px'}}
            />
            <div style={{display: 'flex', gap: '10px'}}>
              <button 
                onClick={handleZipProjectSave} 
                disabled={!zipProjectName.trim()} 
                style={{flex: 1, padding: '12px', background: '#5a6fd6', color: 'white', border: 'none', borderRadius: '8px', cursor: zipProjectName.trim() ? 'pointer' : 'not-allowed', fontWeight: 'bold'}}
              >
                Create Project
              </button>
              <button 
                onClick={() => {setShowZipProjectModal(false); setZipFile(null); setZipProjectName(''); if (zipInputRef.current) zipInputRef.current.value = '';}} 
                style={{padding: '12px 20px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer'}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            width: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 10px 0', color: '#5a6fd6', textAlign: 'center' }}>
              {authMode === 'login' ? 'Login to LaTeX Lab' : 'Create Account'}
            </h2>
            <p style={{ margin: '0 0 30px 0', color: '#64748b', textAlign: 'center', fontSize: '14px' }}>
              {authMode === 'login' ? 'Welcome back!' : 'Start creating amazing documents'}
            </p>

            {authMode === 'signup' && (
              <input
                type="text"
                placeholder="Full Name"
                value={authForm.name}
                onChange={(e) => setAuthForm({...authForm, name: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  marginBottom: '15px',
                  boxSizing: 'border-box'
                }}
              />
            )}

            <input
              type="email"
              placeholder="Email"
              value={authForm.email}
              onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '15px',
                boxSizing: 'border-box'
              }}
            />

            <input
              type="password"
              placeholder="Password"
              value={authForm.password}
              onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
              onKeyPress={(e) => e.key === 'Enter' && (authMode === 'login' ? handleLogin() : handleSignup())}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />

            <button
              onClick={authMode === 'login' ? handleLogin : handleSignup}
              disabled={isAuthenticating}
              style={{
                width: '100%',
                padding: '12px',
                background: isAuthenticating ? '#94a3b8' : '#5a6fd6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isAuthenticating ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '15px'
              }}
            >
              {isAuthenticating ? 'Please wait...' : (authMode === 'login' ? 'Login' : 'Sign Up')}
            </button>

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => {
                  setAuthMode(authMode === 'login' ? 'signup' : 'login');
                  setError(null);
                  setAuthForm({ name: '', email: '', password: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#5a6fd6',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline'
                }}
              >
                {authMode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Login'}
              </button>
            </div>

            {!user && (
              <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                  onClick={() => setShowAuthModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Continue without login
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save Project Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#5a6fd6' }}>Save Project</h2>
            
            <input
              type="text"
              placeholder="Enter project name..."
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && saveProject()}
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setProjectName('');
                }}
                style={{
                  padding: '6px 12px',
                  background: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveProject}
                disabled={isSaving || !projectName.trim()}
                style={{
                  padding: '6px 12px',
                  background: (isSaving || !projectName.trim()) ? '#94a3b8' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (isSaving || !projectName.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Projects Modal */}
      {showProjectsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '700px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, color: '#5a6fd6' }}>My Projects</h2>
              <button 
                onClick={() => setShowProjectsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            {projects.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#94a3b8'
              }}>
                No projects yet. Create your first project!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {projects.map(project => (
                  <div
                    key={project.id}
                    style={{
                      padding: '20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#5a6fd6';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div>
                      <h3 style={{ margin: '0 0 5px 0', color: '#1e293b' }}>
                        {project.name}
                      </h3>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                        Last modified: {new Date(project.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => loadProject(project.id)}
                        style={{
                          padding: '8px 16px',
                          background: '#5a6fd6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Open
                      </button>
                      <button
                        onClick={() => deleteProject(project.id, project.name)}
                        style={{
                          padding: '8px 16px',
                          background: '#c0392b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '900px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, color: '#5a6fd6' }}>Choose a Template</h2>
              <button 
                onClick={() => setShowTemplates(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    padding: '8px 16px',
                    background: selectedCategory === cat ? '#5a6fd6' : '#e2e8f0',
                    color: selectedCategory === cat ? 'white' : '#475569',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '15px'
            }}>
              {filteredTemplates.map(template => (
                <div
                  key={template.id}
                  onClick={() => loadTemplate(template.id)}
                  style={{
                    padding: '20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: 'white'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#5a6fd6';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h3 style={{ 
                    margin: '0 0 10px 0', 
                    color: '#1e293b',
                    fontSize: '16px'
                  }}>
                    {template.name}
                  </h3>
                  <p style={{ 
                    margin: '0 0 10px 0', 
                    color: '#64748b',
                    fontSize: '14px'
                  }}>
                    {template.description}
                  </p>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    background: '#f1f5f9',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#475569',
                    fontWeight: '500'
                  }}>
                    {template.publisher || template.category}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#5a6fd6' }}>Create New Folder</h2>
            
            <input
              type="text"
              placeholder="Folder name..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                  setSelectedParentPath('');
                }}
                style={{
                  padding: '6px 12px',
                  background: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                style={{
                  padding: '6px 12px',
                  background: !newFolderName.trim() ? '#94a3b8' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: !newFolderName.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create File Modal */}
      {showCreateFileModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#5a6fd6' }}>Create New File</h2>
            
            <input
              type="text"
              placeholder="File name (e.g., chapter1.tex)..."
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFile()}
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateFileModal(false);
                  setNewFileName('');
                  setSelectedParentPath('');
                }}
                style={{
                  padding: '6px 12px',
                  background: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFile}
                disabled={!newFileName.trim()}
                style={{
                  padding: '6px 12px',
                  background: !newFileName.trim() ? '#94a3b8' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: !newFileName.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && renameTarget && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#5a6fd6' }}>
              Rename {renameTarget.type === 'folder' ? 'Folder' : 'File'}
            </h2>
            
            <input
              type="text"
              placeholder="New name..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleRename()}
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRenameModal(false);
                  setRenameTarget(null);
                  setNewName('');
                }}
                style={{
                  padding: '6px 12px',
                  background: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                disabled={!newName.trim() || newName === renameTarget.name}
                style={{
                  padding: '6px 12px',
                  background: (!newName.trim() || newName === renameTarget.name) ? '#94a3b8' : '#5a6fd6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!newName.trim() || newName === renameTarget.name) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && moveTarget && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: '12px',
            padding: '30px', width: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 8px 0', color: '#5a6fd6' }}>📂 Move File</h2>
            <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px' }}>
              Moving: <strong>{moveTarget.name}</strong>
            </p>
            <label style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>
              Destination folder path:
            </label>
            <input
              type="text"
              placeholder="e.g. images  or  chapters/intro  (leave empty for root)"
              value={moveDestination}
              onChange={(e) => setMoveDestination(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleMove()}
              autoFocus
              style={{
                width: '100%', padding: '12px',
                border: '2px solid #e2e8f0', borderRadius: '8px',
                fontSize: '14px', marginTop: '8px', marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowMoveModal(false); setMoveTarget(null); setMoveDestination(''); }}
                style={{
                  padding: '6px 12px', background: '#e2e8f0',
                  color: '#475569', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '14px'
                }}
              >Cancel</button>
              <button
                onClick={handleMove}
                style={{
                  padding: '6px 12px', background: '#5a6fd6',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                }}
              >Move</button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }} onClick={() => setShowShortcuts(false)}>
          <div style={{
            background: 'white', borderRadius: '12px',
            padding: '30px', width: '480px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px 0', color: '#5a6fd6' }}>⌨️ Keyboard Shortcuts</h2>
            {[
              ['Ctrl + Enter', 'Compile PDF'],
              ['Ctrl + S', 'Save Project'],
              ['Ctrl + D', 'Download PDF'],
              ['Ctrl + Z', 'Undo'],
              ['Ctrl + Shift + Z', 'Redo'],
              ['Ctrl + /', 'Toggle Comment'],
              ['Ctrl + F', 'Find in Editor'],
              ['Ctrl + H', 'Find & Replace'],
              ['Tab', 'Accept Autocomplete Suggestion'],
              ['\\begin{env}', 'Auto-closes with \\end{env}'],
            ].map(([key, desc]) => (
              <div key={key} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '10px 0', borderBottom: '1px solid #f1f5f9',
                fontSize: '14px'
              }}>
                <code style={{
                  background: '#f1f5f9', padding: '3px 10px',
                  borderRadius: '5px', color: '#334155', fontWeight: '600'
                }}>{key}</code>
                <span style={{ color: '#475569' }}>{desc}</span>
              </div>
            ))}
            <button
              onClick={() => setShowShortcuts(false)}
              style={{
                marginTop: '20px', width: '100%', padding: '10px',
                background: '#5a6fd6', color: 'white', border: 'none',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '500'
              }}
            >Close</button>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 2000,
            minWidth: '180px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.item.type === 'folder' && (
            <>
              <div
                onClick={() => openCreateFileModal(contextMenu.item.path)}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#334155',
                  borderBottom: '1px solid #f1f5f9'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                📄 New File
              </div>
              <div
                onClick={() => openCreateFolderModal(contextMenu.item.path)}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#334155',
                  borderBottom: '1px solid #f1f5f9'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                📁 New Folder
              </div>
            </>
          )}

          {/* Image conversion disabled - feature needs proper implementation */}
          {false && contextMenu.item.type === 'image' && (() => {
            const ext = contextMenu.item.name.split('.').pop().toLowerCase();
            return ['png', 'jpg', 'jpeg'].includes(ext);
          })() && (
            <>
              <div style={{
                padding: '8px 15px',
                fontSize: '12px',
                color: '#94a3b8',
                fontWeight: '600',
                borderBottom: '1px solid #f1f5f9'
              }}>
                🖼️ IMAGE OPTIONS
              </div>
              <div
                onClick={() => handleConvertImage(contextMenu.item.path, 'png')}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#334155',
                  borderBottom: '1px solid #f1f5f9'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                🔄 Convert to PNG
              </div>
              <div
                onClick={() => handleConvertImage(contextMenu.item.path, 'pdf')}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#334155',
                  borderBottom: '1px solid #f1f5f9'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                🔄 Convert to PDF
              </div>
              <div
                onClick={() => handleConvertImage(contextMenu.item.path, 'eps')}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#334155',
                  borderBottom: '1px solid #f1f5f9'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                🔄 Convert to EPS
              </div>
            </>
          )}

          {/* Rename and Delete - available for all types */}
          <div
            onClick={() => openRenameModal(contextMenu.item)}
            style={{
              padding: '10px 15px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#334155',
              borderBottom: '1px solid #f1f5f9'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
          >
            ✏️ Rename
          </div>
          <div
            onClick={() => {
              setMoveTarget(contextMenu.item);
              setMoveDestination('');
              setShowMoveModal(true);
              setContextMenu(null);
            }}
            style={{
              padding: '10px 15px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#334155',
              borderBottom: '1px solid #f1f5f9'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
          >
            📂 Move to Folder
          </div>
          <div
            onClick={() => {
              handleDeleteFile(contextMenu.item.path, contextMenu.item.type === 'folder');
              setContextMenu(null);
            }}
            style={{
              padding: '10px 15px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#c0392b'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
          >
            🗑️ Delete
          </div>
        </div>
      )}

      {/* Improved Error Display */}
      {error && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          maxWidth: '500px',
          backgroundColor: '#fee2e2',
          border: '2px solid #c0392b',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          zIndex: 1000,
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            marginBottom: '15px'
          }}>
            <h3 style={{
              margin: 0,
              color: '#dc2626',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              ⚠️ Compilation Error
            </h3>
            <button
              onClick={() => {
                setError(null);
                setErrorDetails(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#dc2626',
                padding: '0',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>

          {errorDetails && errorDetails.length > 0 ? (
            <div>
              {errorDetails.map((err, idx) => (
                <div key={idx} style={{
                  marginBottom: idx < errorDetails.length - 1 ? '15px' : '0',
                  paddingBottom: idx < errorDetails.length - 1 ? '15px' : '0',
                  borderBottom: idx < errorDetails.length - 1 ? '1px solid #fca5a5' : 'none'
                }}>
                  {err.lineNumber && (
                    <div style={{
                      fontSize: '12px',
                      color: '#991b1b',
                      fontWeight: 'bold',
                      marginBottom: '5px'
                    }}>
                      📍 Line {err.lineNumber}
                    </div>
                  )}
                  
                  <div style={{
                    color: '#991b1b',
                    fontWeight: '500',
                    marginBottom: '8px',
                    fontSize: '14px'
                  }}>
                    {err.message}
                  </div>
                  
                  {err.hint && (
                    <div style={{
                      backgroundColor: '#fef3c7',
                      border: '1px solid #d68910',
                      borderRadius: '6px',
                      padding: '10px',
                      fontSize: '13px',
                      color: '#92400e',
                      lineHeight: '1.5'
                    }}>
                      {err.hint}
                    </div>
                  )}
                  
                  {err.context && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#7f1d1d',
                      fontFamily: 'monospace',
                      backgroundColor: '#fef2f2',
                      padding: '8px',
                      borderRadius: '4px',
                      overflowX: 'auto'
                    }}>
                      {err.context}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              color: '#991b1b',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={() => setShowErrorLog(!showErrorLog)}
            style={{
              marginTop: '15px',
              padding: '8px 16px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {showErrorLog ? '📄 Hide Full Log' : '📄 Show Full Log'}
          </button>
        </div>
      )}

      {uploadedImages.length > 0 && (
        <div style={{
          margin: '0 20px',
          padding: '10px 15px',
          background: '#dbeafe',
          border: '1px solid #3b82f6',
          borderRadius: '8px',
          color: '#1e40af',
          fontSize: '14px'
        }}>
          📎 {uploadedImages.length} image(s) uploaded
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ 
        display: 'flex', 
        flex: 1, 
        gap: '0', 
        overflow: 'hidden'
      }}>
        {/* File Tree Sidebar */}
        {currentProjectId && (
          <div style={{
            width: '280px',
            background: theme.sidebar,
            borderRight: `1px solid ${theme.border}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '15px 20px',
              background: theme.headerBg,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: '600', color: theme.text, fontSize: '14px' }}>
                    {projects.find(p => p.id === currentProjectId)?.name || projectName || 'Untitled Project'}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.mutedText, marginTop: '2px' }}>
                    {currentFilePath ? `📄 ${currentFilePath}` : 'No file selected'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => openCreateFileModal('')}
                    title="New File"
                    style={{
                      padding: '4px 8px',
                      background: isDarkTheme ? '#2d2d4a' : '#e2e8f0',
                      color: isDarkTheme ? '#cdd6f4' : '#475569',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    📄
                  </button>
                  <button
                    onClick={() => openCreateFolderModal('')}
                    title="New Folder"
                    style={{
                      padding: '4px 8px',
                      background: isDarkTheme ? '#2d2d4a' : '#e2e8f0',
                      color: isDarkTheme ? '#cdd6f4' : '#475569',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    📁
                  </button>
              </div>
            </div>
          </div>
            
            <div style={{ flex: 1, overflow: 'auto', padding: '10px' }}>
              {fileTree.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px', 
                  color: '#94a3b8',
                  fontSize: '14px'
                }}>
                  No files yet. Create your first file!
                </div>
              ) : (
                fileTree.map(item => renderFileTreeItem(item))
              )}
            </div>
          </div>
        )}

        {/* Editor */}
        <div style={{
          flex: 1,
          background: theme.bg,
          borderRadius: currentProjectId ? '0' : '12px',
          margin: currentProjectId ? '0' : '20px',
          overflow: 'hidden',
          boxShadow: currentProjectId ? 'none' : '0 4px 20px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '10px 20px',
            background: theme.headerBg,
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '13px', color: theme.mutedText, fontWeight: '500', minWidth: '80px' }}>
              LaTeX Editor
            </span>
            <span style={{ fontWeight: '700', color: theme.text, fontSize: '15px', textAlign: 'center', flex: 1 }}>
              {currentProjectId
                ? (projects.find(p => p.id === currentProjectId)?.name || projectName || 'Untitled Project')
                : ''}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '80px', justifyContent: 'flex-end' }}>
              {currentFilePath && (
                <span style={{ fontSize: '12px', color: theme.mutedText, fontWeight: 'normal' }}>
                  {currentFilePath}
                </span>
              )}
              <button
                onClick={() => setIsDarkTheme(v => !v)}
                title={isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                style={{
                  padding: '3px 7px',
                  background: 'none',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  flexShrink: 0
                }}
              >
                {isDarkTheme ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'4px',padding:'6px 10px',background:isDarkTheme?'#1a1a2e':'#f1f5f9',borderBottom:`1px solid ${theme.border}`}}>
            {[
              {label:'B',title:'Bold',cmd:'\\textbf{',end:'}'},
              {label:'I',title:'Italic',cmd:'\\textit{',end:'}'},
              {label:'U',title:'Underline',cmd:'\\underline{',end:'}'},
              {label:'TT',title:'Typewriter',cmd:'\\texttt{',end:'}'},
              {label:'S1',title:'Section',cmd:'\\section{',end:'}'},
              {label:'S2',title:'Subsection',cmd:'\\subsection{',end:'}'},
              {label:'* List',title:'Itemize',cmd:'\\begin{itemize}\n  \\item ',end:'\n\\end{itemize}'},
              {label:'1. List',title:'Enumerate',cmd:'\\begin{enumerate}\n  \\item ',end:'\n\\end{enumerate}'},
              {label:'Fig',title:'Figure',cmd:'\\begin{figure}[h]\n  \\centering\n  \\includegraphics[width=0.8\\linewidth]{',end:'}\n  \\caption{Caption}\n  \\label{fig:label}\n\\end{figure}'},
              {label:'Table',title:'Table',cmd:'\\begin{table}[h]\n  \\centering\n  \\begin{tabular}{|c|c|c|}\n    \\hline\n    A & B & C \\\\\n    \\hline\n    1 & 2 & 3 \\\\\n    \\hline\n  \\end{tabular}\n  \\caption{Caption}\n\\end{table}',end:''},
              {label:'$',title:'Inline Math',cmd:'$',end:'$'},
              {label:'$$',title:'Display Math',cmd:'\\[\n  ',end:'\n\\]'},
              {label:'cite',title:'Citation',cmd:'\\cite{',end:'}'},
              {label:'ref',title:'Ref',cmd:'\\ref{',end:'}'},
              {label:'label',title:'Label',cmd:'\\label{',end:'}'},
            ].map(({label,title,cmd,end}) => (
              <button key={title} title={title} onClick={() => {
                const editor = editorRef.current;
                if (!editor) return;
                const sel = savedSelectionRef.current || editor.getSelection();
                const selText = editor.getModel().getValueInRange(sel);
                const txt = selText ? cmd+selText+end : cmd+end;
                editor.executeEdits('tb',[{range:sel,text:txt,forceMoveMarkers:true}]);
                editor.focus();
              }} style={{padding:'3px 8px',fontSize:'12px',cursor:'pointer',background:isDarkTheme?'#2d2d4a':'white',border:`1px solid ${isDarkTheme?'#4a4a6a':'#cbd5e1'}`,borderRadius:'4px',color:isDarkTheme?'#cdd6f4':'#334155'}}>{label}</button>
            ))}
            {/* AI Dropdown Button - temporarily hidden */}
            <div style={{position:'relative',display:'inline-block',display:'none'}} className="ai-dropdown-wrap">
              <button
                onClick={() => {
                  const d = document.getElementById('ai-dropdown');
                  d.style.display = d.style.display === 'block' ? 'none' : 'block';
                }}
                style={{padding:'3px 10px',fontSize:'12px',cursor:'pointer',background:'#5a6fd6',border:'none',borderRadius:'4px',color:'white',fontWeight:'600'}}
              >✨ AI ▾</button>
              <div id="ai-dropdown" style={{display:'none',position:'absolute',top:'28px',left:'0',background:'white',border:'1px solid #cbd5e1',borderRadius:'6px',boxShadow:'0 4px 12px rgba(0,0,0,0.15)',zIndex:1000,minWidth:'140px'}}>
                {['Improve','Grammar','Formal','Expand','Summarize'].map(action => (
                  <div key={action}
                    onClick={async () => {
                      document.getElementById('ai-dropdown').style.display = 'none';
                      const editor = editorRef.current;
                      if (!editor) return;
                      const sel = savedSelectionRef.current || editor.getSelection();
                      const selectedText = editor.getModel().getValueInRange(sel);
                      if (!selectedText) { alert('Please select some text first!'); return; }
                      const token = localStorage.getItem('token');
                      if (!token) { alert('Please login to use AI features!'); return; }
                      try {
                        const res = await fetch(`${API_BASE}/api/ai/improve`, {
                          method: 'POST',
                          headers: {'Content-Type':'application/json','Authorization':'Bearer '+token},
                          body: JSON.stringify({text:selectedText, action:action.toLowerCase()})
                        });
                        const data = await res.json();
                        if (data.result) {
                          editor.executeEdits('ai',[{range:sel,text:data.result,forceMoveMarkers:true}]);
                          editor.focus();
                        } else { alert('AI failed. Try again!'); }
                      } catch(err) { alert('AI request failed!'); }
                    }}
                    style={{padding:'8px 14px',cursor:'pointer',fontSize:'13px',color:'#334155',borderBottom:'1px solid #f1f5f9'}}
                    onMouseEnter={e => e.currentTarget.style.background='#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background='white'}
                  >✨ {action}</div>
                ))}
              </div>
            </div>
          </div>
          <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
          <Editor
            height="100%"
            defaultLanguage="latex"
            value={latexCode}
            onChange={(value) => setLatexCode(value)}
            onMount={handleEditorDidMount}
            theme={isDarkTheme ? 'vs-dark' : 'vs-light'}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              suggestOnTriggerCharacters: true,
              quickSuggestions: true,
              wordBasedSuggestions: true,
              wordWrap: 'on',
              acceptSuggestionOnEnter: 'on',
              tabCompletion: 'on',
              find: { addExtraSpaceOnTop: false },
              folding: true,
              foldingHighlight: true,
            }}
          />
          <div style={{padding:'4px 14px',background:theme.headerBg,borderTop:`1px solid ${theme.border}`,fontSize:'12px',color:theme.mutedText,display:'flex',gap:'20px',flexShrink:0}}>
            <span>Words: {latexCode ? latexCode.replace(/%[^\n]*/g,'').replace(/\\[a-zA-Z]+/g,'').replace(/[{}\[\]$&]/g,'').trim().split(/\s+/).filter(w=>w.length>0).length : 0}</span>
            <span>Chars: {latexCode ? latexCode.length : 0}</span>
            <span>Lines: {latexCode ? latexCode.split('\n').length : 0}</span>
            {currentFilePath && <span>File: {currentFilePath.split('/').pop()}</span>}
            {autoSaveStatus === 'saving' && <span style={{color:'#d68910'}}>💾 Saving...</span>}
            {autoSaveStatus === 'saved' && <span style={{color:'#27ae60'}}>✓ Saved</span>}
          </div>
          </div>
        </div>

        {/* PDF Preview */}
        <div style={{
          flex: 1,
          background: theme.bg,
          borderRadius: currentProjectId ? '0' : '12px',
          margin: currentProjectId ? '0' : '20px 20px 20px 0',
          overflow: 'hidden',
          boxShadow: currentProjectId ? 'none' : '0 4px 20px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '12px 20px',
            background: theme.headerBg,
            borderBottom: `1px solid ${theme.border}`,
            fontWeight: '600',
            color: theme.mutedText,
            fontSize: '13px'
          }}>
            PDF Preview
          </div>
          <div style={{ flex: 1 }}>
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="PDF Preview"
              />
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: theme.mutedText,
                fontSize: '16px'
              }}>
                {latexCode ? 'Click "Compile PDF" to generate preview' : 'Select a template to get started'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toast.type === 'success' ? '#27ae60' : '#c0392b',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '15px',
          fontWeight: '500',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <span style={{ fontSize: '20px' }}>
            {toast.type === 'success' ? '✅' : '❌'}
          </span>
          {toast.message}
        </div>
      )}

      {/* Animation CSS */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default LatexEditor;
